import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CustomCredentialFlow } from '../components/CustomCredentialFlow';
import { LayoutGrid, Plug, MessageSquare, UserCircle, Map, Activity, LayoutTemplate, FolderOpen, Monitor, ChevronDown, Bookmark, Plus, Settings, Paperclip, Send, X, Search, Clock, FileText, Folder, Play, Pause, CheckCircle2, Sparkles, ArrowRight, ChevronLeft, ChevronRight, ListChecks, Globe, Loader2, Lock, Users, Zap, Star, MoreHorizontal, Info, Pencil, Trash2, Download, Sun, Moon, History, AlertTriangle, Filter, Check, Bell, Minus, ChevronUp, ChevronsUpDown, PhoneCall, GraduationCap, Layers, CreditCard, LogOut } from 'lucide-react';
import { BillingModal } from '../components/BillingModal';
import { TracksView } from '../components/dashboard/TracksView';
import { GroupsView } from '../components/dashboard/GroupsView';
import { TablesView } from '../components/dashboard/TablesView';
import { TRACKS } from '../data/workspaceTracks';
// Recent chats shown in the sidebar. Kept short on purpose — it's a "pick up
// where you left off" list, not an archive (that's the Chats view).
const SIDEBAR_CHATS = ['SideShift connection check', 'Company Brain campaign brief', 'Q3 pipeline review', 'Creator outreach draft'];
// ── Activity log types ───────────────────────────────────────────────────────
// A system-wide, read-only feed of everything Giga did on your behalf. The
// source of one entry is the kind of automation that triggered it; the status
// is the outcome of that run. Both drive the Activity view's filters.
type ActivityType = 'Chat' | 'Pulse' | 'Journey' | 'Recipe' | 'Assistant';
type ActivityStatus = 'success' | 'running' | 'needs-attention';
// Timing filter ranges.
type ActivityRange = 'all' | '7d' | '30d' | '90d' | '6m' | '12m' | 'custom';
type ActivityEntry = {
  task: string;
  detail: string;
  type: ActivityType;
  status: ActivityStatus;
  source: string;
  tool: string;
  // Days ago used for date grouping and the timing filter.
  daysAgo: number;
  time: string;
};
// ── Starter prompt categories & data ────────────────────────────────────────
type StarterCategory = 'comms' | 'operations' | 'admin' | 'growth' | 'insights';
type PlanSite = {
  slug: string;
  label: string;
};
type StarterPrompt = {
  title: string;
  desc: string;
  tools: string[];
  // The domains/tools the plan will act on, shown under "Allow actions on…".
  sites: PlanSite[];
  // The step-by-step approach Giga proposes before acting.
  steps: string[];
};
const STARTER_CATEGORIES: {
  id: StarterCategory;
  label: string;
}[] = [{
  id: 'comms',
  label: 'Comms'
}, {
  id: 'operations',
  label: 'Operations'
}, {
  id: 'admin',
  label: 'Admin'
}, {
  id: 'growth',
  label: 'Growth'
}, {
  id: 'insights',
  label: 'Insights'
}];
const STARTER_PROMPTS: Record<StarterCategory, StarterPrompt[]> = {
  comms: [{
    title: 'Meeting recaps',
    desc: 'When a meeting transcript lands, create tasks for action items and email a recap to attendees.',
    tools: ['gmail', 'asana', 'slack'],
    sites: [{
      slug: 'asana',
      label: 'Asana'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Pull the latest meeting transcript and identify the action items', 'Create an Asana task for each action item with its owner and due date', 'Draft a concise recap summarizing decisions and next steps', 'Email the recap to every attendee from Gmail', 'Post a short summary to the relevant Slack channel']
  }, {
    title: 'Slack reply nudges',
    desc: "When I'm mentioned in a thread but don't reply within 2 hours, text me a reminder with context.",
    tools: ['slack'],
    sites: [{
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch every Slack thread where you are @-mentioned', 'Start a 2-hour timer the moment a mention lands', 'Check whether you have replied before the timer runs out', 'If not, summarize who asked and what they need', 'Send you a reminder with a direct link to the thread']
  }, {
    title: 'Daily briefing',
    desc: 'Send me a morning briefing pulled from my Calendar, Gmail, and open Asana tasks.',
    tools: ['google', 'gmail', 'asana'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ["Pull today's events from Google Calendar", 'Scan Gmail for unread or flagged messages that need a reply', 'List Asana tasks due today or overdue', 'Compose a single, scannable morning briefing', 'Deliver it to you at 8:00am each weekday']
  }],
  operations: [{
    title: 'Protect focus blocks',
    desc: 'On a fragmented day, see if meetings can shift to open up focus blocks.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }],
    steps: ["Review today's calendar for fragmentation and gaps", 'Identify meetings that could be moved without conflicts', 'Find at least one 90-minute focus block to protect', 'Draft reschedule proposals for the movable meetings', 'Ask for your approval before sending any invites']
  }, {
    title: 'Bug triage pipeline',
    desc: 'When a Sentry error spikes, open a GitHub issue and post the details to Slack.',
    tools: ['sentry', 'github', 'slack'],
    sites: [{
      slug: 'sentry',
      label: 'Sentry'
    }, {
      slug: 'github',
      label: 'GitHub'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Detect when a Sentry error crosses its spike threshold', 'Gather the stack trace, affected users, and release info', 'Open a GitHub issue with the details and a triage label', 'Post an alert with the issue link to the #engineering channel', 'Tag the most likely owner based on recent commits']
  }, {
    title: 'Stale issue sweep',
    desc: 'Each week, find Linear issues with no activity in 14 days and ping their owners.',
    tools: ['linear', 'slack'],
    sites: [{
      slug: 'linear',
      label: 'Linear'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Query Linear for issues with no activity in the last 14 days', 'Group the stale issues by assignee', 'Draft a friendly nudge for each owner with their issue list', 'Send each nudge as a Slack DM', 'Post a summary of the sweep to the team channel']
  }],
  admin: [{
    title: 'Friday inbox cleanup',
    desc: 'Every Friday, archive non-actionable emails and flag anything that needs a reply.',
    tools: ['gmail'],
    sites: [{
      slug: 'gmail',
      label: 'Gmail'
    }],
    steps: ["Scan this week's Gmail inbox and classify each message", 'Archive newsletters and notifications that need no action', 'Flag messages that still need a reply from you', 'Draft quick replies for the most routine threads', 'Send you a short summary of what was cleaned up']
  }, {
    title: 'Invoice reconciliation',
    desc: 'When a payment lands in Stripe, match it to an invoice and update the books.',
    tools: ['stripe', 'notion'],
    sites: [{
      slug: 'stripe',
      label: 'Stripe'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Detect the new payment as it lands in Stripe', 'Match the amount and customer to an open invoice', 'Mark the matched invoice as paid', 'Update the bookkeeping ledger in Notion', 'Flag any payment that has no matching invoice']
  }, {
    title: 'Document filing',
    desc: 'Sort new files into the right Drive folders and rename them to our convention.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Drive'
    }],
    steps: ['Watch the Drive inbox folder for newly added files', 'Read each file to infer its type and project', 'Rename it to match the team naming convention', 'Move it into the correct project folder', 'Log anything ambiguous for you to confirm']
  }],
  growth: [{
    title: 'Ad spend audit',
    desc: 'Pause ad sets under 0.8x ROAS in Meta Ads and export a summary PDF.',
    tools: ['meta', 'notion'],
    sites: [{
      slug: 'meta',
      label: 'Meta Ads'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Pull the last 14 days of spend and ROAS from Meta Ads', 'Flag every ad set performing under 0.8× ROAS', 'Pause the flagged ad sets to stop the bleed', 'Export a summary PDF of what changed and why', 'Save the report to Notion and notify you']
  }, {
    title: 'New deal onboarding',
    desc: 'When a deal closes, create the client workspace and send the welcome sequence.',
    tools: ['notion', 'gmail', 'asana'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ['Detect the closed-won deal and pull the client details', 'Spin up the client onboarding workspace in Notion', 'Create the standard onboarding tasks in Asana', 'Send the welcome email sequence from Gmail', 'Notify the account owner that onboarding has kicked off']
  }, {
    title: 'Lead enrichment',
    desc: 'Enrich new CRM contacts with firmographics and route hot ones to sales.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch Attio for newly added contacts', 'Enrich each one with firmographic and role data', 'Score the lead against your ideal-customer profile', 'Update the CRM record with the enriched fields', 'Route hot leads to sales in the #pipeline channel']
  }],
  insights: [{
    title: 'Weekly report generation',
    desc: 'Every Monday, pull last week metrics and post a digest to #leadership.',
    tools: ['notion', 'slack'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ["Pull last week's metrics from the Notion dashboards", 'Compare them against the prior week and targets', 'Highlight the biggest wins and the risks to watch', 'Compose a concise leadership digest', 'Post it to the #leadership channel every Monday at 9am']
  }, {
    title: 'Pipeline cleanup',
    desc: 'Flag stale or at-risk deals and summarize what changed since last week.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Scan the Attio pipeline for deals with no recent activity', 'Flag deals slipping past their expected close date', 'Summarize what changed on each deal since last week', 'Suggest a next step for every at-risk deal', 'Post the cleanup summary to the sales channel']
  }, {
    title: 'Support trends',
    desc: 'Cluster this week support tickets by theme and surface the top issues.',
    tools: ['crisp', 'notion'],
    sites: [{
      slug: 'crisp',
      label: 'Crisp'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ["Pull this week's resolved and open tickets from Crisp", 'Cluster the tickets by underlying theme', 'Rank the themes by volume and customer impact', 'Write up the top three issues with example tickets', 'Save the trends report to Notion for the team']
  }]
};
// Small tool/site icon chip reused across the rail.
function ToolIcon({
  slug,
  size = 'sm'
}: {
  slug: string;
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'w-6 h-6' : 'w-5 h-5';
  const img = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return <span className={`${box} rounded-md bg-white border border-neutral-200 flex items-center justify-center shrink-0`} data-id="element-7907" data-mp="3p99sthpun1iw">
      <img src={`https://cdn.simpleicons.org/${slug}/475569`} className={`${img} object-contain`} alt="" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
    }} data-id="element-7908" data-mp="3kdkz3v0igeui" />
    </span>;
}
// Claude-style plan flow: shows the picked task as a message, "thinks" for a
// beat, then reveals a numbered plan with the sites it will touch and
// Approve / Make changes actions.
function PlanView({
  prompt,
  onBack
}: {
  prompt: StarterPrompt;
  onBack: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState<'thinking' | 'plan'>(reducedMotion ? 'plan' : 'thinking');
  const [approved, setApproved] = useState(false);
  useEffect(() => {
    if (reducedMotion) {
      setPhase('plan');
      return;
    }
    setPhase('thinking');
    const id = setTimeout(() => setPhase('plan'), 1100);
    return () => clearTimeout(id);
  }, [prompt, reducedMotion]);
  return <div className="flex flex-col" data-id="element-7909" data-mp="3gssony19ukbs">
      {/* Back affordance */}
      <button onClick={onBack} className="self-start flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors mb-4" data-id="element-7910" data-mp="3lsnd7uxkqxto">
        <ChevronLeft size={14} data-id="element-7911" data-mp="25grta09wjxn2" />
        Back to suggestions
      </button>

      {/* User message bubble */}
      <div className="flex justify-end mb-5" data-id="element-7912" data-mp="2abdujam9s9tf">
        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#2B211A] text-white text-sm leading-relaxed px-3.5 py-2.5" data-id="element-7913" data-mp="sfc29d8nm10v">
          {prompt.desc}
        </div>
      </div>

      {/* Working / creating plan */}
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4" data-id="element-7914" data-mp="11fig5089l30k">
        {phase === 'thinking' ? <>
            <Loader2 size={15} className="text-[#EA580C] animate-spin" data-id="element-7915" data-mp="xfzsvxsz0fuk" />
            Creating plan…
          </> : <>
            <Sparkles size={15} className="text-[#EA580C]" data-id="element-7916" data-mp="9n9jnwcgcanz" />
            Here&rsquo;s the plan
          </>}
      </div>

      <AnimatePresence data-id="element-7917" data-mp="ihpbnthzsl12">
        {phase === 'plan' && <motion.div initial={reducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: reducedMotion ? 0 : 0.4,
        ease: 'easeOut'
      }} className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-id="element-7918" data-mp="iiqwgpot8lvz">
            {/* Plan header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100" data-id="element-7919" data-mp="3uvpx51fwhaz8">
              <ListChecks size={16} className="text-neutral-500" data-id="element-7920" data-mp="3pf0dhhxawdp" />
              <span className="text-sm font-semibold text-neutral-900" data-id="element-7921" data-mp="8nq9fq0e92uv">
                Giga&rsquo;s plan
              </span>
            </div>

            {/* Sites it will act on */}
            <div className="px-4 py-3 border-b border-neutral-100" data-id="element-7922" data-mp="dfzxt1lv1uv1">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2.5" data-id="element-7923" data-mp="icq818azhw8j">
                <Globe size={12} data-id="element-7924" data-mp="rdy89vx29759" />
                Allow actions on these
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-7925" data-mp="rc50gdapogvo">
                {prompt.sites.map(site => <span key={site.slug} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-700" data-id="element-7926" data-mp="wc9elwo5cziq">
                    <ToolIcon slug={site.slug} data-id="element-7927" data-mp="2y4ny306cac19" />
                    {site.label}
                  </span>)}
              </div>
            </div>

            {/* Numbered approach */}
            <div className="px-4 py-3.5" data-id="element-7928" data-mp="2ltehqujcik64">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-3" data-id="element-7929" data-mp="2lsbtkudeqvtw">
                Approach to follow
              </div>
              <ol className="flex flex-col gap-3" data-id="element-7930" data-mp="2bxbipxjsj1ne">
                {prompt.steps.map((step, i) => <motion.li key={i} initial={reducedMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: -6
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: reducedMotion ? 0 : 0.3,
              delay: reducedMotion ? 0 : 0.15 + i * 0.08
            }} className="flex items-start gap-3" data-id="element-7931" data-mp="39ipkghk9aomw" data-mpi={i}>
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border border-neutral-300 flex items-center justify-center text-[11px] font-semibold text-neutral-500" data-id="element-7932" data-mp="1o77blg7e8jrn">
                      {i + 1}
                    </span>
                    <span className="text-sm text-neutral-700 leading-snug" data-id="element-7933" data-mp="b61fmv61rki0">
                      {step}
                    </span>
                  </motion.li>)}
              </ol>
            </div>

            {/* Actions */}
            <div className="px-4 pb-4 pt-1" data-id="element-7934" data-mp="68j7h6zut9b2">
              {approved ? <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-[#0F9D58]/10 text-[#0F9D58] text-sm font-semibold" data-id="element-7935" data-mp="66yc5f5ptmc5">
                  <CheckCircle2 size={16} data-id="element-7936" data-mp="3sfr5tpccwfxl" />
                  Plan approved — Giga is on it
                </div> : <div className="flex flex-col gap-2" data-id="element-7937" data-mp="3eoupawvpfp6w">
                  <button onClick={() => setApproved(true)} className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-7938" data-mp="3eq5r4r5lamvd">
                    Approve plan
                  </button>
                  <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-7939" data-mp="39o6oxkll5w00">
                    Make changes
                  </button>
                </div>}
              <p className="text-[11px] text-neutral-400 leading-relaxed mt-3" data-id="element-7940" data-mp="302oxzk1awm1c">
                Giga will only use the tools listed. You&rsquo;ll be asked
                before anything else.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ── Chat context controls: Space + Journey pickers ──────────────────────────
// The "+ Space" and "+ Journey" buttons above a chat open lightweight pickers:
// Space is a compact menu of workspaces with a check on the active one;
// Journey is a searchable popover listing the journeys you can attach. Both
// close on outside-click or Escape.
const CHAT_SPACES = ['Everyone', 'NoContact Ops', 'No Contact Private', 'NoContact Tech – Product & Engineering', 'Giga Master', 'Giga Gtm'];
const CHAT_JOURNEYS = ['Early User Activation', 'Ryan Paid Ads', 'Getting Giga Out There', 'Product Backlog', 'Giga Creator Outreach', 'Getting Operator Stable'];
function ChatContextControls() {
  // Which picker is open, if any.
  const [open, setOpen] = useState<null | 'space' | 'journey'>(null);
  const [activeSpace, setActiveSpace] = useState('Everyone');
  const [journeyQuery, setJourneyQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Close on outside-click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // Focus the journey search when that popover opens.
  useEffect(() => {
    if (open === 'journey') {
      const id = window.setTimeout(() => searchRef.current?.focus(), 20);
      return () => window.clearTimeout(id);
    }
  }, [open]);
  const filteredJourneys = CHAT_JOURNEYS.filter(j => j.toLowerCase().includes(journeyQuery.trim().toLowerCase()));
  const triggerClass = (isOpen: boolean) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isOpen ? 'bg-neutral-200 text-neutral-900' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'}`;
  return <div ref={wrapRef} className="relative flex items-center gap-2" data-id="element-7941" data-mp="2shs8fe4zdago">
      {/* Space picker */}
      <div className="relative" data-id="element-7942" data-mp="2nip2lrh6f07n">
        <button onClick={() => setOpen(open === 'space' ? null : 'space')} aria-haspopup="menu" aria-expanded={open === 'space'} className={triggerClass(open === 'space')} data-id="element-7943" data-mp="2ngul6n545q8b">
          <Plus size={14} data-id="element-7944" data-mp="2hazoag0l2xyy" />
          Space
        </button>
        <AnimatePresence data-id="element-7945" data-mp="2h8desau4u8e3">
          {open === 'space' && <motion.div role="menu" initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-72 origin-top-left rounded-xl bg-[#4B463F] text-white shadow-2xl ring-1 ring-black/20 p-1.5" data-id="element-7946" data-mp="2ccoeg5aagngq">
              <button role="menuitem" onClick={() => setOpen(null)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition-colors" data-id="element-7947" data-mp="1on3qt936x214">
                <Plus size={15} className="text-white/70" data-id="element-7948" data-mp="1fk2n1f1srn0m" />
                New space
              </button>
              <div className="my-1 h-px bg-white/10" data-id="element-7949" data-mp="1amt149dm9thj" />
              {CHAT_SPACES.map(s => {
            const active = activeSpace === s;
            return <button key={s} role="menuitemradio" aria-checked={active} onClick={() => {
              setActiveSpace(s);
              setOpen(null);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/90 hover:bg-white/10 transition-colors text-left" data-id="element-7950" data-mp="3r8pl6f83cot5">
                    <span className="w-4 shrink-0 flex items-center justify-center" data-id="element-7951" data-mp="33jeuxrodact9">
                      {active && <Check size={15} className="text-white" data-id="element-7952" data-mp="37kgu68iaaov8" />}
                    </span>
                    <span className="truncate" data-id="element-7953" data-mp="3cjti6wkw61ze">{s}</span>
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Journey picker */}
      <div className="relative" data-id="element-7954" data-mp="3hcmi975f0m55">
        <button onClick={() => setOpen(open === 'journey' ? null : 'journey')} aria-haspopup="dialog" aria-expanded={open === 'journey'} className={triggerClass(open === 'journey')} data-id="element-7955" data-mp="2jvp8lzz794rf">
          <Plus size={14} data-id="element-7956" data-mp="3s565tzjlwifj" />
          Journey
        </button>
        <AnimatePresence data-id="element-7957" data-mp="3s3bmuqrwb0e3">
          {open === 'journey' && <motion.div initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-80 origin-top-left rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 p-2.5" data-id="element-7958" data-mp="3n6l6s3g3c4so">
              <div className="relative mb-1" data-id="element-7959" data-mp="349k10gepq0no">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7960" data-mp="3i81i3va44ewm" />
                <input ref={searchRef} type="text" value={journeyQuery} onChange={e => setJourneyQuery(e.target.value)} placeholder="Search journeys…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-7961" data-mp="3d7447uw4y70x" />
              </div>
              <div className="max-h-60 overflow-y-auto pt-1 flex flex-col gap-1" data-id="element-7962" data-mp="150iohbbz93gr">
                {filteredJourneys.length === 0 ? <p className="px-3 py-6 text-center text-xs text-neutral-400" data-id="element-7963" data-mp="r1srrwt2l5sx">
                    No journeys match “{journeyQuery}”.
                  </p> : filteredJourneys.map(j => <button key={j} onClick={() => {
              setOpen(null);
              setJourneyQuery('');
            }} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-800 hover:bg-neutral-100 transition-colors truncate" data-id="element-7964" data-mp="1jon0jwgiygqk">
                      {j}
                    </button>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
type DashboardVariant = 'current' | 'calm-deferred' | 'progressive' | 'chat-first';
export function Dashboard() {
  // DEBUG: Variant switcher for comparing dashboard directions.
  // Defaults to the Progressive Disclosure direction (first in the toggle).
  const [variant, setVariant] = useState<DashboardVariant>('progressive');
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Deep links: ?view=<navId> opens the dashboard directly on that view, so
  // other surfaces (the web chat's sidebar) can link to the right page rather
  // than dumping the user on the generic overview.
  const viewParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
  const [activeNav, setActiveNav] = useState(viewParam || 'overview');
  // ── Sidebar content vs. sidebar settings ────────────────────────────────
  // Tracks and Chats are the things people open all day, so they live in the
  // sidebar as real lists. The CONNECT / MANAGE / BRAIN nav is configuration
  // you visit occasionally, so it collapses behind one disclosure rather than
  // sitting above the content as 12 permanent rows.
  const [tracksExpanded, setTracksExpanded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [starterCat, setStarterCat] = useState<StarterCategory>('comms');
  const [activePlan, setActivePlan] = useState<StarterPrompt | null>(null);
  // Whether the "Create a pulse" slide-over panel is open.
  const [pulseCreateOpen, setPulseCreateOpen] = useState(false);
  // Whether the "Create a journey" slide-over panel is open.
  const [journeyCreateOpen, setJourneyCreateOpen] = useState(false);
  // Whether the "Create an assistant" slide-over panel is open.
  const [assistantCreateOpen, setAssistantCreateOpen] = useState(false);
  // Selected category in the Recipes filter tabs ('All' = no filter).
  const [recipeCategory, setRecipeCategory] = useState('All');
  // Selected function in the Assistants filter tabs ('All' = no filter).
  const [assistantCategory, setAssistantCategory] = useState('All');
  // ── Chat tab strip (right panel) ────────────────────────────────────────
  // The chat panel hosts one or more open chat sessions as closeable tabs.
  // Closing a tab removes it; if it was the active one, focus falls to the
  // neighboring tab. Closing the LAST tab leaves the panel in a calm empty
  // state ("No active chats") with a button to start a fresh one.
  type ChatTab = {
    id: number;
    label: string;
  };
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([{
    id: 1,
    label: 'New Chat'
  }, {
    id: 2,
    label: 'Session 39200'
  }]);
  const [activeChatId, setActiveChatId] = useState<number>(1);
  const nextChatIdRef = useRef(3);
  // In the "calm-deferred" and "chat-first" directions the persistent right
  // chat panel is hidden on load; it only appears once the user explicitly
  // enters a task. This boolean tracks that deferred-entry state.
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  // In the "progressive" direction the overview swings between a first-time
  // guided setup and the everyday returning dashboard. We lift that state up
  // here so the right chat panel can stay hidden during first-time setup —
  // a brand-new user shouldn't be greeted by a chat they haven't earned yet.
  const [progressiveState, setProgressiveState] = useState<'first' | 'returning'>('first');
  const openNewChat = () => {
    const id = nextChatIdRef.current++;
    setChatTabs(tabs => [...tabs, {
      id,
      label: 'New Chat'
    }]);
    setActiveChatId(id);
    setActivePlan(null);
    setChatPanelOpen(true);
  };
  const closeChat = (id: number) => {
    setChatTabs(tabs => {
      const idx = tabs.findIndex(t => t.id === id);
      const next = tabs.filter(t => t.id !== id);
      // If the closed tab was active, move focus to the neighbor (prefer the
      // tab to the left, falling back to the new first tab).
      if (id === activeChatId) {
        const fallback = next[idx - 1] ?? next[0];
        setActiveChatId(fallback ? fallback.id : -1);
        setActivePlan(null);
      }
      if (next.length === 0) {
        setChatPanelOpen(false);
      }
      return next;
    });
  };
  // Integrations tab controls
  const [intView, setIntView] = useState<'app' | 'list'>('app');
  const [intTab, setIntTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [intQuery, setIntQuery] = useState('');
  // Activity log filters
  const [activityType, setActivityType] = useState<ActivityType | 'all'>('all');
  const [activityStatus, setActivityStatus] = useState<ActivityStatus | 'all'>('all');
  // Timing filter: which date buckets to include in the Activity feed.
  const [activityRange, setActivityRange] = useState<ActivityRange>('all');
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  // Bottom-left account menu + the billing / manage-subscription modal it opens.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const navGroups = [{
    items: [{
      id: 'overview',
      label: 'Home',
      icon: LayoutGrid
    }]
  }, {
    label: 'CONNECT',
    items: [{
      id: 'integrations',
      label: 'Integrations',
      icon: Plug
    }, {
      id: 'power-ups',
      label: 'Power Ups',
      icon: Zap
    }]
  }, {
    label: 'MANAGE',
    items: [{
      id: 'groups',
      label: 'Groups',
      icon: Layers
    }, {
      id: 'spaces-tracks',
      label: 'Tracks',
      icon: Map
    }, {
      id: 'assistants',
      label: 'Assistants',
      icon: UserCircle
    }, {
      id: 'skills',
      label: 'Skills',
      icon: GraduationCap
    }, {
      id: 'journeys',
      label: 'Workflows',
      icon: ListChecks
    }, {
      id: 'pulses',
      label: 'Pulses',
      icon: Activity
    }, {
      id: 'conversations',
      label: 'Chats',
      icon: MessageSquare
    }]
  }, {
    label: 'BRAIN',
    items: [{
      id: 'files',
      label: 'Files',
      icon: FolderOpen
    }, {
      id: 'tables',
      label: 'Tables',
      icon: Layers
    }, {
      id: 'viewer',
      label: 'Viewer',
      icon: Monitor
    }]
  }];
  // Everything except Home lives behind the "Manage workspace" disclosure —
  // Tracks and Chats are surfaced above as real content lists instead.
  const managedNavGroups = navGroups.filter(g => !!g.label);
  const managedNavActive = managedNavGroups.some(g => g.items.some(i => i.id === activeNav)) && !activeTrack && !activeChat;
  // Active tracks only, alphabetical — the sidebar is a shortcut list, not the
  // full filterable library (that's the Tracks view).
  const sidebarTracks = TRACKS.filter(t => t.status === 'active').slice().sort((a, b) => a.name.localeCompare(b.name));
  const integrations = [{
    name: 'AgentMail',
    desc: 'productivity',
    icon: 'gmail',
    connected: false
  }, {
    name: 'Airtable',
    desc: 'productivity',
    icon: 'airtable',
    connected: false
  }, {
    name: 'Anthropic',
    desc: 'Claude models and the Anthropic API.',
    icon: 'anthropic',
    connected: false
  }, {
    name: 'Asana',
    desc: '1 account connected',
    icon: 'asana',
    connected: true
  }, {
    name: 'Attio',
    desc: 'sales',
    icon: 'attio',
    connected: false
  }, {
    name: 'Cloudflare',
    desc: 'infrastructure',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Cloudflare R2',
    desc: 'S3-compatible object storage with …',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Crisp',
    desc: '1 account connected',
    icon: 'crisp',
    connected: true
  }, {
    name: 'Custom',
    desc: 'Credentials for services without a d…',
    icon: 'box',
    connected: false
  }, {
    name: 'Expo',
    desc: '1 account connected',
    icon: 'expo',
    connected: true
  }, {
    name: 'Facebook',
    desc: 'marketing',
    icon: 'facebook',
    connected: false
  }, {
    name: 'Folk',
    desc: 'sales',
    icon: 'box',
    connected: false
  }, {
    name: 'GitHub',
    desc: '2 accounts connected',
    icon: 'github',
    connected: true
  }, {
    name: 'Google Gemini',
    desc: 'ai',
    icon: 'googlegemini',
    connected: false
  }, {
    name: 'Google Workspace',
    desc: 'Gmail, Drive, Calendar, Docs, Sheets.',
    icon: 'google',
    connected: false
  }, {
    name: 'Granola',
    desc: 'AI meeting notepad. Search transcri…',
    icon: 'box',
    connected: false
  }, {
    name: 'Growi',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Heroku',
    desc: 'infrastructure',
    icon: 'heroku',
    connected: false
  }, {
    name: 'Linear',
    desc: 'Issue tracking and project managem…',
    icon: 'linear',
    connected: false
  }, {
    name: 'Magic Patterns',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Meta Ads',
    desc: 'Facebook + Instagram ad managem…',
    icon: 'meta',
    connected: false
  }, {
    name: 'Notion',
    desc: 'Pages, databases, and structured d…',
    icon: 'notion',
    connected: false
  }, {
    name: 'OpenAI',
    desc: 'GPT models and the OpenAI API.',
    icon: 'openai',
    connected: false
  }, {
    name: 'OpenRouter',
    desc: 'ai',
    icon: 'box',
    connected: false
  }, {
    name: 'Postgres',
    desc: 'Open-source relational database.',
    icon: 'postgresql',
    connected: false
  }, {
    name: 'RevenueCat',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Sentry',
    desc: '1 account connected',
    icon: 'sentry',
    connected: true
  }];
  // The handful most teams connect first — a curated discovery shortlist so the
  // long list isn't the only way in. Matched by name against `integrations`.
  const POPULAR_INTEGRATIONS = new Set(['Google Workspace', 'Slack', 'AgentMail', 'Notion', 'GitHub', 'Asana', 'Linear', 'Meta Ads', 'Airtable', 'Attio']);
  return (
    // Flat, calm product surface. We intentionally avoid the
    // `min-h-screen bg-[#FAF8F4]` combination here: a global rule in index.css
    // stamps the marketing halftone dot-texture onto any such container, which
    // made the dashboard read "old school." `h-screen` + an explicit cream
    // canvas keeps the app chrome clean and modern. Using the brand cream
    // (#FAF8F4) — lighter than the previous #F4F2EE — so it reads as a very
    // subtle warm accent rather than a heavy tint.
    <div className={`h-screen bg-[#FAF8F4] flex font-body overflow-hidden ${isDarkMode ? 'dark-dashboard' : ''}`} data-id="element-7965" data-mp="jslxeypd5ey6">
      {isDarkMode && <style data-id="element-7966" data-mp="a0zcubr4y3ir">{`
          .dark-dashboard {
            color-scheme: dark;
          }
          .dark-dashboard .bg-white { background-color: #160F0A !important; }
          .dark-dashboard .bg-\\[\\#FAF8F4\\] { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-50 { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-100 { background-color: #2B211A !important; }
          .dark-dashboard .bg-neutral-200 { background-color: #6B5D52 !important; }
          .dark-dashboard .bg-\\[\\#2B211A\\] { background-color: #EAE4D9 !important; }
          
          .dark-dashboard .text-neutral-900 { color: #F4F0EA !important; }
          .dark-dashboard .text-neutral-800 { color: #EAE4D9 !important; }
          .dark-dashboard .text-neutral-700 { color: #E7E0D7 !important; }
          .dark-dashboard .text-neutral-600 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-500 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-400 { color: #6B5D52 !important; }
          .dark-dashboard .text-\\[\\#2B211A\\] { color: #F4F0EA !important; }
          .dark-dashboard .text-white { color: #160F0A !important; }
          
          .dark-dashboard .border-neutral-100 { border-color: #1A120C !important; }
          .dark-dashboard .border-neutral-200 { border-color: #2B211A !important; }
          .dark-dashboard .border-neutral-300 { border-color: #6B5D52 !important; }
          
          .dark-dashboard .hover\\:bg-neutral-50:hover { background-color: #1A120C !important; }
          .dark-dashboard .hover\\:bg-neutral-100:hover { background-color: #2B211A !important; }
          .dark-dashboard .hover\\:bg-neutral-200:hover { background-color: #6B5D52 !important; }
          .dark-dashboard .hover\\:bg-\\[\\#1A1410\\]:hover { background-color: #D5CFC5 !important; }
          
          .dark-dashboard .hover\\:text-neutral-900:hover { color: #F4F0EA !important; }
          .dark-dashboard .hover\\:text-neutral-700:hover { color: #EAE4D9 !important; }
          .dark-dashboard .hover\\:text-neutral-600:hover { color: #E7E0D7 !important; }
          .dark-dashboard .hover\\:text-\\[\\#2B211A\\]:hover { color: #F4F0EA !important; }
          
          /* Fix simpleicons inversion in dark mode */
          .dark-dashboard img[src*="simpleicons.org"] { filter: brightness(0) invert(1) opacity(0.8); }
        `}</style>}
      {/* Left Sidebar */}
      <div className="w-[240px] bg-white border-r border-[#EFE7DA] flex flex-col shrink-0 h-screen" data-id="element-7967" data-mp="1hbaqgfs8r3q9">
        {/* Workspace Switcher */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-[#EFE7DA] cursor-pointer hover:bg-[#FAF8F4] transition-colors duration-200 ease-out" data-id="element-7968" data-mp="18b5j25n1s5c3">
          <div className="flex items-center gap-2.5" data-id="element-7969" data-mp="13ef3djf5nbr7">
            <div className="w-6 h-6 rounded-lg bg-[#2B211A] flex items-center justify-center text-white text-xs font-bold" data-id="element-7970" data-mp="ye9karmyaxkq">
              G
            </div>
            <span className="font-semibold text-sm text-[#2B211A]" data-id="element-7971" data-mp="onel6molw3hv">
              Giga Main
            </span>
          </div>
          <ChevronDown size={14} className="text-[#A2917F]" data-id="element-7972" data-mp="kmcu4t4uexqz" />
        </div>

        {/* Nav — content first (Tracks, Chats), configuration collapsed below */}
        <div className="flex-1 py-3 px-3 flex flex-col gap-4 overflow-y-auto" data-id="element-7973" data-mp="fot3suuveuio">
          {/* Home */}
          <button onClick={() => {
            setActiveNav('overview');
            setActiveTrack(null);
            setActiveChat(null);
          }} className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ease-out ${activeNav === 'overview' && !activeTrack && !activeChat ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A]'}`} data-id="element-7974" data-mp="apzr34ner7jk">
            <LayoutGrid size={18} strokeWidth={activeNav === 'overview' ? 2.5 : 2} data-id="element-7975" data-mp="1ftu0lnfkddqi" />
            Home
          </button>

          {/* Tracks — the working library. Capped at 12 with a Show more, so a
           long list never pushes Chats out of view. */}
          <div className="flex flex-col gap-0.5" data-id="element-7976" data-mp="16uqesbp0ik8b">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-7977" data-mp="1auhc6y91nylt">
              {/* Sentence case, not all-caps letterspaced. A shouted micro-label
                above every group is the loudest dated tell in an app sidebar,
                and it is competing with the items it is labelling. */}
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} className="text-[11.5px] font-bold text-[#A2917F] transition-colors hover:text-[#2B211A]" data-id="element-7978" data-mp="i7r4evw77bpx">
                Tracks
              </button>
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} aria-label="All tracks" className="text-[#C4B4A2] transition-colors hover:text-[#2B211A]" data-id="element-7979" data-mp="s7hwys177gnd">
                <Plus size={13} data-id="element-7980" data-mp="116v77mx5c995" />
              </button>
            </div>
            {sidebarTracks.slice(0, tracksExpanded ? sidebarTracks.length : 12).map(t => {
              const isActive = activeTrack === t.name;
              return <button key={t.name} onClick={() => {
                setActiveTrack(t.name);
                setActiveChat(null);
                setActiveNav('spaces-tracks');
              }} title={t.name} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-[13.5px] transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A] font-medium'}`} data-id="element-7981" data-mp="1avwpxkv2t4qt">
                  <Folder size={14} className={isActive ? 'text-white shrink-0' : 'text-[#B6A895] shrink-0'} data-id="element-7982" data-mp="1nj8uv2mpk8s5" />
                  <span className="truncate" data-id="element-7983" data-mp="1xbejq3hnya34">{t.name}</span>
                </button>;
            })}
            {sidebarTracks.length > 12 && <button type="button" onClick={() => setTracksExpanded(v => !v)} className="px-3 py-1.5 text-left text-[13px] font-medium text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-7984" data-mp="1sk50plqme507">
                {tracksExpanded ? 'Show less' : `Show ${sidebarTracks.length - 12} more`}
              </button>}
          </div>

          {/* Chats */}
          <div className="flex flex-col gap-0.5" data-id="element-7985" data-mp="2g9hdswp2ix7s">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-7986" data-mp="2bamk2zpkbo8w">
              <button type="button" onClick={() => setActiveNav('conversations')} className="text-[11.5px] font-bold text-[#A2917F] transition-colors hover:text-[#2B211A]" data-id="element-7987" data-mp="lw84b4wivbpj">
                Chats
              </button>
              <button type="button" onClick={() => setActiveNav('conversations')} aria-label="New chat" className="text-[#C4B4A2] transition-colors hover:text-[#2B211A]" data-id="element-7988" data-mp="2sjyoeyor3ayx">
                <Plus size={13} data-id="element-7989" data-mp="2xle8anf6zbfq" />
              </button>
            </div>
            {SIDEBAR_CHATS.map(c => {
              const isActive = activeChat === c;
              return <button key={c} onClick={() => {
                setActiveChat(c);
                setActiveTrack(null);
                setActiveNav('conversations');
              }} title={c} className={`flex items-center px-3 py-1.5 rounded-xl text-[13.5px] transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A] font-medium'}`} data-id="element-7990" data-mp="2ns761bfr0bjv">
                  <span className="truncate" data-id="element-7991" data-mp="24x0c8d7nyp6t">{c}</span>
                </button>;
            })}
          </div>

          {/* Everything else — one disclosure instead of three always-open
           groups. Opens automatically when one of its views is active. */}
          <div className="mt-auto border-t border-neutral-200 pt-3" data-id="element-7992" data-mp="1nf1e18mcic6">
            <button type="button" onClick={() => setManageOpen(o => !o)} aria-expanded={manageOpen} className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-7993" data-mp="3ssycp36pqfl5">
              <span className="flex items-center gap-3" data-id="element-7994" data-mp="3iwvdo675qx2f">
                <Settings size={18} strokeWidth={2} data-id="element-7995" data-mp="kbk3l49mnv8v" />
                Manage workspace
              </span>
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${manageOpen || managedNavActive ? 'rotate-180' : ''}`} data-id="element-7996" data-mp="feah9xhjbzp9" />
            </button>
            <AnimatePresence initial={false} data-id="element-7997" data-mp="5mxon3nwx5i6">
              {(manageOpen || managedNavActive) && <motion.div key="manage-nav" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-7998" data-mp="133wg2jtzwcwq">
                  <div className="flex flex-col gap-3 pt-2" data-id="element-7999" data-mp="1flpbedh3hne5">
                    {managedNavGroups.map((group, i) => <div key={i} className="flex flex-col gap-0.5" data-id="element-8000" data-mp="1kll6fn7te4hk" data-mpi={i}>
                        {group.label && <div className="px-3 py-1 text-[11.5px] font-bold text-[#A2917F]" data-id="element-8001" data-mp="1pkeg14jzdbcg">
                            {group.label}
                          </div>}
                        {group.items.map(item => {
                      const isActive = activeNav === item.id && !activeTrack && !activeChat;
                      return <button key={item.id} onClick={() => {
                        setActiveNav(item.id);
                        setActiveTrack(null);
                        setActiveChat(null);
                      }} className={`flex items-center gap-3 rounded-xl px-3 py-1.5 text-[13.5px] font-medium transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A]'}`} data-id="element-8002" data-mp="23c2wfk19ilz8">
                              <item.icon size={16} strokeWidth={isActive ? 2.5 : 2} data-id="element-8003" data-mp="34zx0ubnfdn8k" />
                              {item.label}
                            </button>;
                    })}
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* User Profile — clicking the row opens an account menu (above it)
            with a cog affordance and access to Billing / manage subscription. */}
        <div className="relative p-4 border-t border-neutral-200" data-id="element-8004" data-mp="304hpbqcw43fb">
          <button type="button" onClick={() => setAccountMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={accountMenuOpen} className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 transition-colors ${accountMenuOpen ? 'bg-neutral-100' : 'hover:bg-neutral-100'}`} data-id="element-8005" data-mp="2w0te8xp1uppb">
            
            <span className="flex items-center gap-3 min-w-0" data-id="element-8006" data-mp="iud9kg4of6mq">
              <span className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-bold text-xs shrink-0" data-id="element-8007" data-mp="ntq0bbyo4xul">
                F
              </span>
              <span className="flex flex-col min-w-0 text-left" data-id="element-8008" data-mp="8ytj498vt05d">
                <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-8009" data-mp="dwbjhb8u271r">
                  Faith Alqahtani
                </span>
                <span className="text-xs text-neutral-500 truncate" data-id="element-8010" data-mp="11ngzprior1cl">
                  faith@example.com
                </span>
              </span>
            </span>
            <ChevronsUpDown size={15} className={`shrink-0 transition-colors ${accountMenuOpen ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-8011" data-mp="rurzxtwhzbx3" />
            
          </button>

          <AnimatePresence data-id="element-8012" data-mp="1kjqhopwpkc1v">
            {accountMenuOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setAccountMenuOpen(false)} data-id="element-8013" data-mp="1pap3g50gu2ci" />
              
                <motion.div role="menu" initial={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} transition={{
                duration: 0.14,
                ease: 'easeOut'
              }} className="absolute bottom-[calc(100%-0.5rem)] left-4 right-4 z-50 origin-bottom overflow-hidden rounded-xl border border-neutral-200 bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.5)]" data-id="element-8014" data-mp="1z6fh6rqymp89">
                
                  <button role="menuitem" type="button" onClick={() => {
                  setAccountMenuOpen(false);
                  setBillingOpen(true);
                }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-8015" data-mp="16fxgtyhmf1yj">
                  
                    <CreditCard size={16} className="text-neutral-500" data-id="element-8016" data-mp="346420pzglh0h" />
                    Usage &amp; billing
                  </button>
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-8017" data-mp="39347ozsechr2">
                  
                    <Settings size={16} className="text-neutral-500" data-id="element-8018" data-mp="2lfw2siyvrapk" />
                    Settings
                  </button>
                  <div className="my-1 h-px bg-neutral-100" data-id="element-8019" data-mp="2qepcs1eykjkz" />
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100" data-id="element-8020" data-mp="2uf9ezjee2ddz">
                  
                    <LogOut size={16} className="text-neutral-500" data-id="element-8021" data-mp="26q6w6880qehw" />
                    Log out
                  </button>
                </motion.div>
              </>}
          </AnimatePresence>
        </div>
      </div>

      {/* Billing / manage-subscription modal */}
      <BillingModal open={billingOpen} onClose={() => setBillingOpen(false)} data-id="element-8022" data-mp="v5m79wn6etm7" />

      {/* Main Content Column */}
      <div className="flex-1 flex flex-col min-w-0 h-screen" data-id="element-8023" data-mp="19yz715fkma82">
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shrink-0" data-id="element-8024" data-mp="150nuuo1sznmo">
          <div className="flex items-center gap-4" data-id="element-8025" data-mp="1izduobg0c1eq">
            <button className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100" data-id="element-8026" data-mp="1e1lz97g5lx5s">
              <LayoutGrid size={16} className="text-neutral-400" data-id="element-8027" data-mp="1nrx9tcucf4ln" />
              All Spaces
              <ChevronDown size={14} className="text-neutral-400" data-id="element-8028" data-mp="21rq4tkcwk6zf" />
            </button>
            <div className="w-px h-4 bg-neutral-200" data-id="element-8029" data-mp="1xqwhas3dgu72" />
            <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8030" data-mp="1xs7m8v8k07zr">
              <Bookmark size={18} data-id="element-8031" data-mp="2gfgezpk9d4n0" />
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-8032" data-mp="2le9t9k7dczo8">
            <div className="relative" data-id="element-8033" data-mp="2lbxchadckvcr">
              <button onClick={() => setWhatsNewOpen(!whatsNewOpen)} className={`p-2 transition-colors rounded-lg ${whatsNewOpen ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'}`} aria-label="What's new" data-id="element-8034" data-mp="27es1cvys16an">
                <div className="relative" data-id="element-8035" data-mp="1xmcjdynhchpr">
                  <Bell size={18} data-id="element-8036" data-mp="22ffdq902zv70" />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#EA580C] border border-white" data-id="element-8037" data-mp="22gqkmhotpj2c" />
                </div>
              </button>
              <AnimatePresence data-id="element-8038" data-mp="1sokt192hh3no">
                {whatsNewOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setWhatsNewOpen(false)} data-id="element-8039" data-mp="1epl5y5umcwtm" />
                    <motion.div initial={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute right-0 top-full mt-2 w-80 z-50 bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden" data-id="element-8040" data-mp="1joo73f7haiwb">
                      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50" data-id="element-8041" data-mp="24xwe74x7hrc5">
                        <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8042" data-mp="29p60pwngkejt">
                          What's new
                        </h3>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto p-2 flex flex-col gap-1" data-id="element-8043" data-mp="2iq48mq10p67o">
                        {WHATS_NEW.map((n, i) => <div key={i} className="flex items-start gap-3 rounded-lg hover:bg-neutral-50 px-3 py-2.5 transition-colors cursor-pointer" data-id="element-8044" data-mp="2nonvqtrcko1m" data-mpi={i}>
                            <span className={`shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${n.tag === 'New' ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-8045" data-mp="2xuf9j6blciom">
                              {n.tag}
                            </span>
                            <div data-id="element-8046" data-mp="2jv5zkodzakt3">
                              <div className="text-sm font-semibold text-neutral-900" data-id="element-8047" data-mp="3gjc65gugnna0">
                                {n.title}
                              </div>
                              <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed" data-id="element-8048" data-mp="3bmlst1a1j5sa">
                                {n.detail}
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors rounded-lg hover:bg-neutral-100" aria-label="Toggle dark mode" data-id="element-8049" data-mp="3uibu0ma5fy54">
              {isDarkMode ? <Sun size={18} data-id="element-8050" data-mp="1xkebd0ocmxm9" /> : <Moon size={18} data-id="element-8051" data-mp="1xn0gktnrp11b" />}
            </button>
            <button onClick={openNewChat} className="flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8052" data-mp="1iwt7v5nkawdt">
              <Plus size={16} data-id="element-8053" data-mp="19utnq4h93c6n" />
              New chat
            </button>
          </div>
        </header>

        {/* Dashboard Content Area */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-8054" data-mp="1evr7umltmgas">
          {/* The content surface. Was a 12px-radius card with a cool grey
            border and a flat `shadow-sm`; now the site's own 22px radius, warm
            hairline and two-part shadow, so the app screen reads as the same
            product as the page it is shown on. */}
          <div className="bg-white rounded-[22px] border border-[#EFE7DA] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-16px_rgba(43,33,26,0.12)] min-h-full p-8" data-id="element-8055" data-mp="2zat4a6n783wb">
            {/* Overview — rendered per the active debug variant. */}
            {activeNav === 'overview' && variant === 'current' && <CurrentOverview data-id="element-8056" data-mp="xchd71vvxrew" />}
            {activeNav === 'overview' && variant === 'calm-deferred' && <CommandCenterOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-8057" data-mp="smao70xgo4w4" />}
            {activeNav === 'overview' && variant === 'progressive' && <EmptyStateOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-8058" data-mp="16bulhr89w9y1" />}
            {activeNav === 'overview' && variant === 'chat-first' && <ChatFirstOverview onStartTask={openNewChat} data-id="element-8059" data-mp="1gblf7qp0ekx2" />}

            {/* Activity */}
            {activeNav === 'activity' && <ActivityView typeFilter={activityType} setTypeFilter={setActivityType} statusFilter={activityStatus} setStatusFilter={setActivityStatus} rangeFilter={activityRange} setRangeFilter={setActivityRange} data-id="element-8060" data-mp="1bc0jrq91sup3" />}

            {/* Integrations */}
            {activeNav === 'integrations' && <div className="flex flex-col" data-id="element-8061" data-mp="3el2emr206n8c">
                <div className="flex items-center justify-between mb-6" data-id="element-8062" data-mp="3ek97vq31nxq7">
                  <div className="flex items-center gap-4" data-id="element-8063" data-mp="3jjce3durg9zr">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8064" data-mp="3jlyk39206hfv">
                      Integrations
                    </h1>
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8065" data-mp="3tiuujkbgpdn2">
                      <button onClick={() => setIntView('app')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'app' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8066" data-mp="2vut5sybpnx7o">
                        App View
                      </button>
                      <button onClick={() => setIntView('list')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'list' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8067" data-mp="3ajz48k2q5jei">
                        List View
                      </button>
                    </div>
                  </div>
                  <CustomCredentialFlow data-id="element-8068" data-mp="z5xl6exq5dt1" />
                </div>

                <div className="relative mb-6" data-id="element-8069" data-mp="v7rlfqvazyhm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-8070" data-mp="q7dbg3a56egy" />
                  <input type="text" value={intQuery} onChange={e => setIntQuery(e.target.value)} placeholder={`Search ${integrations.length} integrations`} className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm focus:outline-none focus:border-neutral-300 transition-colors" data-id="element-8071" data-mp="l8itt4nwuvyf" />
                </div>

                <div className="flex items-center justify-between mb-6 border-b border-neutral-100" data-id="element-8072" data-mp="7hx8l9azi9xd">
                  <div className="flex items-center gap-6" data-id="element-8073" data-mp="2iu69wme2ht3">
                    <button onClick={() => setIntTab('all')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'all' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8074" data-mp="3tnsss3wi7l1z">
                      All integrations{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8075" data-mp="3f3q182t21pec">
                        {integrations.length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('popular')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'popular' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8076" data-mp="362i3pmyd4slb">
                      Popular{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8077" data-mp="6kijwk5pkcry">
                        {integrations.filter(x => POPULAR_INTEGRATIONS.has(x.name)).length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('connected')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'connected' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8078" data-mp="3szboa4fq9z44">
                      Connected{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8079" data-mp="2a15jmjkg2ez9">
                        {integrations.filter(x => x.connected).length}
                      </span>
                    </button>
                  </div>
                </div>

                {(() => {
                const visible = integrations.filter(x => {
                  if (intTab === 'connected' && !x.connected) return false;
                  if (intTab === 'popular' && !POPULAR_INTEGRATIONS.has(x.name)) return false;
                  if (intQuery.trim() && !x.name.toLowerCase().includes(intQuery.toLowerCase())) return false;
                  return true;
                });
                if (visible.length === 0) {
                  return <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-8080" data-mp="1w1e3odadaoim">
                        <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-8081" data-mp="37idravlrsf6b">
                          <Search size={18} className="text-neutral-400" data-id="element-8082" data-mp="14wrz9w4cd4hg" />
                        </div>
                        <p className="text-sm font-semibold text-neutral-700" data-id="element-8083" data-mp="19ujpmlpc8ijj">
                          No integrations found
                        </p>
                        <p className="text-xs text-neutral-500 mt-1" data-id="element-8084" data-mp="1jklg1k55yd1m">
                          Try a different search or switch back to all
                          integrations.
                        </p>
                      </div>;
                }
                if (intView === 'list') {
                  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8085" data-mp="1xjclyj9vgseo">
                        <div className="divide-y divide-neutral-100" data-id="element-8086" data-mp="22jh7wotjufca">
                          {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 px-5 py-3.5 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8087" data-mp="36d6cq5hry7e" data-mpi={i}>
                              <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8088" data-mp="856jghgv9n71">
                                <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-8089" data-mp="3rzjf30ckj7cw" />
                              </div>
                              <span className="font-semibold text-neutral-900 text-sm shrink-0 w-44 truncate" data-id="element-8090" data-mp="jhx4s6sq3659">
                                {integration.name}
                              </span>
                              <span className="text-xs text-neutral-500 truncate flex-1" data-id="element-8091" data-mp="9nwqbebjwojv">
                                {integration.desc}
                              </span>
                              {integration.connected ? <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8092" data-mp="2u9yc0taq1pyd">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-8093" data-mp="1p71wnfvx06ow" />
                                  Connected
                                </span> : <span className="text-[11px] font-medium text-neutral-400 px-2 py-0.5 shrink-0" data-id="element-8094" data-mp="1ffo11j733r11">
                                  Not connected
                                </span>}
                            </div>)}
                        </div>
                      </div>;
                }
                return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8095" data-mp="235074a747iz4">
                      {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer bg-white" data-id="element-8096" data-mp="1y543wd68rvkm" data-mpi={i}>
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8097" data-mp="mrif4t4anvzm">
                            <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-8098" data-mp="irrl8gjh0zqv" />
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-8099" data-mp="384ootzkbhun4">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8100" data-mp="3d3i4nyn2txqg">
                              {integration.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-8101" data-mp="3i4pctyt4syxn">
                              {integration.connected && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-8102" data-mp="3m2dc1e1uxdvc" />}
                              <span className="text-xs text-neutral-500 truncate" data-id="element-8103" data-mp="3r0x0bl3xb1qv">
                                {integration.desc}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>;
              })()}
              </div>}

            {/* Conversations */}
            {activeNav === 'conversations' && <ConversationsView data-id="element-8104" data-mp="2v35pf3ux5s9j" />}

            {/* Assistants */}
            {activeNav === 'assistants' && (() => {
              // Assistants are grouped by the business FUNCTION they serve
              // (Sales, Marketing, Support, …) — that's how teams reason
              // about who does what — plus a richer card: what it actually
              // does, the tools it works in, and how much it's handled. The
              // function tabs default to "All", mirroring the Recipes filter.
              const ASSISTANTS = [{
                name: 'Sales Assistant',
                desc: 'Handles outbound, logs every touchpoint, and keeps the CRM clean so reps focus on selling.',
                initial: 'S',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '1.2k tasks',
                tools: ['salesforce', 'gmail', 'slack']
              }, {
                name: 'Pipeline Watcher',
                desc: 'Flags stalled deals and at-risk opportunities before the weekly sync.',
                initial: 'P',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '430 tasks',
                tools: ['hubspot', 'slack']
              }, {
                name: 'Content Writer',
                desc: 'Drafts posts, emails, and landing copy in your brand voice, ready for review.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: true,
                func: 'Marketing',
                tasks: '860 tasks',
                tools: ['notion', 'gmail']
              }, {
                name: 'Campaign Analyst',
                desc: 'Pulls campaign metrics into shareable performance recaps.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: false,
                func: 'Marketing',
                tasks: '210 tasks',
                tools: ['mailchimp', 'meta']
              }, {
                name: 'Support Triage',
                desc: 'Tags incoming tickets, drafts replies from help docs, and escalates the rest.',
                initial: 'S',
                color: 'bg-sky-100 text-sky-700',
                active: true,
                func: 'Support',
                tasks: '2.4k tasks',
                tools: ['zendesk', 'slack']
              }, {
                name: 'Finance Analyst',
                desc: 'Reconciles payments to invoices, categorizes expenses, and builds cash snapshots.',
                initial: 'F',
                color: 'bg-rose-100 text-rose-700',
                active: true,
                func: 'Finance',
                tasks: '540 tasks',
                tools: ['stripe', 'quickbooks']
              }, {
                name: 'Research Bot',
                desc: 'Finds and summarizes market and account data on demand.',
                initial: 'R',
                color: 'bg-emerald-100 text-emerald-700',
                active: true,
                func: 'Operations',
                tasks: '690 tasks',
                tools: ['notion', 'google']
              }, {
                name: 'Ops Manager',
                desc: 'Monitors workflows across tools and flags anything that breaks or stalls.',
                initial: 'O',
                color: 'bg-blue-100 text-blue-700',
                active: false,
                func: 'Operations',
                tasks: '310 tasks',
                tools: ['linear', 'slack']
              }, {
                name: 'PR Summarizer',
                desc: 'Digests new pull requests and posts a daily summary to the team.',
                initial: 'P',
                color: 'bg-indigo-100 text-indigo-700',
                active: true,
                func: 'Engineering',
                tasks: '980 tasks',
                tools: ['github', 'slack']
              }];
              const FUNCTIONS = ['All', ...Array.from(new Set(ASSISTANTS.map(a => a.func)))];
              const visible = assistantCategory === 'All' ? ASSISTANTS : ASSISTANTS.filter(a => a.func === assistantCategory);
              return <div className="flex flex-col" data-id="element-8105" data-mp="insn4lrp5g3i">
                    <div className="flex items-start justify-between gap-4 mb-5" data-id="element-8106" data-mp="imhkip9zmedz">
                      <div data-id="element-8107" data-mp="dr24bqoic6ee">
                        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8108" data-mp="dno70wupandt">
                          Assistants
                        </h1>
                        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8109" data-mp="nmneic3qccz8">
                          Dedicated AI teammates, each focused on one part of
                          the business — with their own instructions, tools, and
                          memory. Filter by function to find the right one.
                        </p>
                      </div>
                      <button onClick={() => setAssistantCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8110" data-mp="2bdl3e438d1bn">
                        <Plus size={16} data-id="element-8111" data-mp="21nt795fhg052" />
                        New assistant
                      </button>
                    </div>

                    {/* Function filter tabs — defaults to "All" */}
                    <div role="tablist" aria-label="Filter assistants by function" className="flex flex-wrap gap-2 mb-6" data-id="element-8112" data-mp="2ub5wxogxwo2l">
                      {FUNCTIONS.map(f => {
                    const count = f === 'All' ? ASSISTANTS.length : ASSISTANTS.filter(a => a.func === f).length;
                    const active = assistantCategory === f;
                    return <button key={f} role="tab" aria-selected={active} onClick={() => setAssistantCategory(f)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8113" data-mp="18ycn0v20npjx">
                            {f}
                            <span className={`ml-1.5 ${active ? 'text-white/60' : 'text-neutral-400'}`} data-id="element-8114" data-mp="1rmadl81nisc6">
                              {count}
                            </span>
                          </button>;
                  })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8115" data-mp="2fb22nqjy2ukq">
                      {visible.map((a, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer bg-white" data-id="element-8116" data-mp="2ajto6g3rcj0p" data-mpi={i}>
                          <div className="flex items-start justify-between mb-3" data-id="element-8117" data-mp="330e6eawi6afn">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base ${a.color}`} data-id="element-8118" data-mp="332h83s70ps07">
                              {a.initial}
                            </div>
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${a.active ? 'text-emerald-600 bg-emerald-50' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-8119" data-mp="3ak5m5tldy3qc">
                              <span className={`w-1.5 h-1.5 rounded-full ${a.active ? 'bg-emerald-500' : 'bg-neutral-400'}`} data-id="element-8120" data-mp="3jld616n5n20w" />
                              {a.active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1" data-id="element-8121" data-mp="3th6unoe5elz1">
                            <h3 className="font-semibold text-neutral-900 text-sm" data-id="element-8122" data-mp="25om595iezud">
                              {a.name}
                            </h3>
                            <span className="text-[10px] font-medium text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded-full" data-id="element-8123" data-mp="b51u1xdzjgcz">
                              {a.func}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-8124" data-mp="1is7kiwi6syop">
                            {a.desc}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-neutral-100" data-id="element-8125" data-mp="18v2x6tclux6b">
                            <div className="flex items-center -space-x-1.5" data-id="element-8126" data-mp="1dto1b3uza101">
                              {a.tools.map((tool, t) => <span key={t} className="w-6 h-6 rounded-md bg-white border border-neutral-200 flex items-center justify-center" title={tool} data-id="element-8127" data-mp="21ji8p5wj2xcw" data-mpi={t}>
                                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3.5 h-3.5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-8128" data-mp="26ko2l71xcqo0" />
                                </span>)}
                            </div>
                            <span className="text-[11px] font-medium text-neutral-400" data-id="element-8129" data-mp="2pb5vbbmrbm9d">
                              {a.tasks}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
            })()}

            {/* Skills */}
            {activeNav === 'skills' && <div className="flex flex-col" data-id="element-8130" data-mp="iku34b3n0ats">
                <div className="flex flex-wrap items-start justify-between gap-6 mb-6" data-id="element-8131" data-mp="in6wdkeui7m5">
                  <div className="min-w-[280px] flex-1" data-id="element-8132" data-mp="3r15qshm65kxj">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8133" data-mp="3m2kp0dr9qt6z">
                      Skills
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8134" data-mp="3wzdx4ou8olg">
                      Reusable capabilities you invoke with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-600" data-id="element-8135" data-mp="1fb33zzsb1sl8">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                  <div className="flex items-center flex-wrap gap-4 shrink-0" data-id="element-8136" data-mp="7xsojgne80ut">
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8137" data-mp="cxf8e8xq8fcl">
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8138" data-mp="gzs74de23czv">
                        Private (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-8139" data-mp="3ldqljlimu723">
                        Global (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8140" data-mp="3ueezyhbxu9fi">
                        Space (0)
                      </button>
                    </div>
                    <div className="w-px h-6 bg-neutral-200" data-id="element-8141" data-mp="31pr8g40linti" />
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8142" data-mp="31phhoe7q8cmr">
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-8143" data-mp="36n9kw4dtnl69">
                        Custom (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8144" data-mp="2u7jk982tkrzw">
                        Giga (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8145" data-mp="2l6bsl8ut6pes">
                        Community (0)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8146" data-mp="2bdxk7x986kjn">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100" data-id="element-8147" data-mp="26eb7rq1818bw">
                    <div className="flex items-center gap-2" data-id="element-8148" data-mp="1xfzknxzkcso7">
                      <button className="p-1 text-neutral-400 hover:text-neutral-600 border-b-2 border-neutral-900 text-neutral-900" data-id="element-8149" data-mp="1xfg4omth4ect">
                        <LayoutGrid size={16} data-id="element-8150" data-mp="3sj4ay11y7f2k" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-8151" data-mp="fbt11yy08sr">
                      <span className="text-xs font-medium text-neutral-400" data-id="element-8152" data-mp="ejyluh30h42">
                        Filter by
                      </span>
                      <div className="flex items-center bg-neutral-100 rounded-md p-0.5" data-id="element-8153" data-mp="3ns5sovxeg6wo">
                        <button className="px-2.5 py-1 text-xs font-medium bg-white rounded shadow-sm text-neutral-900" data-id="element-8154" data-mp="38v51ctj4edyq">
                          None
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8155" data-mp="3drvep93jivgg">
                          Tag
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8156" data-mp="34ttjja6hmx13">
                          Pack
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-8157" data-mp="1d5lnz6x66ke1">
                    <h3 className="text-lg font-semibold text-neutral-400 mb-2" data-id="element-8158" data-mp="187k6gqfe6t4o">
                      No skills yet
                    </h3>
                    <p className="text-sm text-neutral-400" data-id="element-8159" data-mp="1m006rjgwsd1w">
                      Invoke them with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-500" data-id="element-8160" data-mp="1h6ffsbdqgjc7">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                </div>
              </div>}

            {/* Journeys */}
            {activeNav === 'journeys' && <div className="flex flex-col" data-id="element-8161" data-mp="20vpt5iq0as75">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8162" data-mp="20x0y3lv6u5zu">
                  <div data-id="element-8163" data-mp="aii991px89nt">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8164" data-mp="ak1sn16vj0tt">
                      Journeys
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8165" data-mp="6jrhla485k83">
                      Ongoing projects you and your assistant work through
                      together over time — with shared context and
                      back-and-forth as things evolve, not a one-off task.
                    </p>
                  </div>
                  <button onClick={() => setJourneyCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8166" data-mp="2kkqq8htmnuhi">
                    <Plus size={16} data-id="element-8167" data-mp="2tikovzev707u" />
                    Create journey
                  </button>
                </div>

                <div className="flex items-center justify-between mb-6" data-id="element-8168" data-mp="2ydgi32vu7tmj">
                  <div className="flex items-center gap-3" data-id="element-8169" data-mp="339f96y8jvpqn">
                    <button className="p-1 text-neutral-400 hover:text-neutral-600" data-id="element-8170" data-mp="38a2ztisva4kc">
                      <LayoutGrid size={18} data-id="element-8171" data-mp="3cadf5m2ju7bv" />
                    </button>
                    <div className="flex items-center gap-1.5" data-id="element-8172" data-mp="3c8irhzzw8143">
                      {[{
                      initials: 'GM',
                      count: 19,
                      color: 'bg-sky-700'
                    }, {
                      initials: 'GG',
                      count: 8,
                      color: 'bg-neutral-800'
                    }, {
                      initials: 'NC',
                      count: 3,
                      color: 'bg-indigo-600'
                    }, {
                      initials: 'Ev',
                      count: 2,
                      color: 'bg-rose-800'
                    }, {
                      initials: 'NT',
                      count: 1,
                      color: 'bg-neutral-900'
                    }, {
                      initials: 'NO',
                      count: 1,
                      color: 'bg-rose-700'
                    }].map((a, i) => <div key={i} className="relative" data-id="element-8173" data-mp="1ybjzarhp64c8" data-mpi={i}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${a.color} ring-2 ring-white`} data-id="element-8174" data-mp="1y9z738iuuxhj">
                            {a.initials}
                          </div>
                          <div className="absolute -top-2 -right-1.5 bg-white text-neutral-500 text-[9px] font-bold px-1 rounded-full border border-neutral-200" data-id="element-8175" data-mp="xnapemwtjb8r">
                            {a.count}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer" data-id="element-8176" data-mp="1udthg2i9p67h">
                    <input type="checkbox" className="rounded border-neutral-300 text-[#2B211A] focus:ring-[#2B211A]" data-id="element-8177" data-mp="1ge1w1gpf8njg" />
                    Show favourites
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8178" data-mp="4y3sttgxcvq5">
                  <div className="divide-y divide-neutral-100" data-id="element-8179" data-mp="9xzo94bk3eu3">
                    {[{
                    author: 'NC',
                    color: 'bg-indigo-600',
                    name: 'Ryan Paid Ads',
                    desc: "Updated Jun 20 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Getting Giga Out There',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Viktor Product Updates',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Product Backlog',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Giga Creator Outreach',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Twitter Linkedin Content',
                    desc: "Updated Jun 17 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Newsjacking For Giga Content',
                    desc: "Updated Jun 16 '26",
                    status: 'Unset'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Early User Activation',
                    desc: "Created Jun 16 '26 · Updated Jun 16 '26",
                    status: 'In focus',
                    statusColor: 'text-emerald-600'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Getting Operator Stable',
                    desc: "Updated May 22 '26",
                    status: 'Unset'
                  }].map((j, i) => <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer group" data-id="element-8180" data-mp="194jel2z5qvt5" data-mpi={i}>
                        <div className="flex items-center gap-4 min-w-0" data-id="element-8181" data-mp="1e462c6cm3ug7">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${j.color}`} data-id="element-8182" data-mp="21sytlpiahe4h">
                            {j.author}
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-8183" data-mp="1s2w0lpb64065">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8184" data-mp="1x1yx2ld30y2i">
                              {j.name}
                            </span>
                            <span className="text-xs text-neutral-400 truncate" data-id="element-8185" data-mp="2kq8dt8vfuvms">
                              {j.desc}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0" data-id="element-8186" data-mp="2ufyh8mmgursc">
                          <span className={`text-xs font-medium ${j.statusColor || 'text-neutral-400'}`} data-id="element-8187" data-mp="2udufaa84o2up">
                            {j.status}
                          </span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8188" data-mp="2kh98bjcjsho8">
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8189" data-mp="2ghqpqu878iok">
                              <Info size={16} data-id="element-8190" data-mp="26pkwlh67nv7s" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8191" data-mp="21r1756ses1ao">
                              <Star size={16} data-id="element-8192" data-mp="1ws6iho7rfiin" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8193" data-mp="1sptd50wxdmme">
                              <MoreHorizontal size={16} data-id="element-8194" data-mp="87itl77ipuk6" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Pulses */}
            {activeNav === 'pulses' && <div className="flex flex-col" data-id="element-8195" data-mp="m6qwaa2w3kt4">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8196" data-mp="m5phbuir6e64">
                  <div data-id="element-8197" data-mp="3krilbmfh3i4f">
                    <div className="flex items-center gap-2" data-id="element-8198" data-mp="3posbj4aorxtb">
                      <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8199" data-mp="3ppbw6srq10b1">
                        Pulses
                      </h1>
                      <span className="text-sm text-neutral-400 font-medium" data-id="element-8200" data-mp="397jcvdb9i1f">
                        1
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8201" data-mp="31yojlu6v1mi5">
                      Recurring tasks Giga runs on a schedule.
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0" data-id="element-8202" data-mp="1cilwg97g4jvw">
                    <button className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-8203" data-mp="18ha621gqzumn">
                      <Settings size={16} data-id="element-8204" data-mp="1m9yph44zdg3i" />
                      Pulse Settings
                    </button>
                    <button onClick={() => setPulseCreateOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 hover:text-neutral-700 transition-colors" data-id="element-8205" data-mp="1hfcc4vfc9dgs">
                      <Plus size={16} data-id="element-8206" data-mp="1r6p19f9r5pj4" />
                      New pulse
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mb-4" data-id="element-8207" data-mp="20757uxlslcs7">
                  <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer" data-id="element-8208" data-mp="205kal083ffqn">
                    <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-8209" data-mp="autb32vhlt42" />
                    Show disabled
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-8210" data-mp="2hjbqs7hbbq3q">
                  <div className="px-6 py-5 flex items-center justify-between gap-4" data-id="element-8211" data-mp="28onmajjkacfz">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-8212" data-mp="28mkvhxal4fa7">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" data-id="element-8213" data-mp="2copp4w119bwu" />
                      <div className="flex flex-col min-w-0" data-id="element-8214" data-mp="1ypgcg6c1czxm">
                        <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8215" data-mp="23pb1038c9dfi">
                          No Contact Daily Brief
                        </span>
                        <span className="text-xs text-neutral-400 truncate" data-id="element-8216" data-mp="1prw82oivmnis">
                          Daily at 07:00 Etc/UTC
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-5 shrink-0" data-id="element-8217" data-mp="3k1zvl9y3bn9j">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-neutral-400" data-id="element-8218" data-mp="3o4w7kht4kl4i">
                        <Clock size={14} data-id="element-8219" data-mp="10y9u6aiapm8o" />
                        in 19h
                      </div>
                      <button className="flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-8220" data-mp="vwkj4uv2p8kl">
                        <Play size={14} data-id="element-8221" data-mp="mxqovb57sua7" />
                        Run
                      </button>
                      <span className="text-sm font-medium text-neutral-900" data-id="element-8222" data-mp="1jnzmmqyl5vxf">
                        On
                      </span>
                      <div className="flex items-center gap-1" data-id="element-8223" data-mp="1fn6309s6f3ac">
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-8224" data-mp="1auml50mghlzq">
                          <Pencil size={16} data-id="element-8225" data-mp="15vjpu7wa2u4y" />
                        </button>
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-8226" data-mp="23cighlujdxig">
                          <Trash2 size={16} data-id="element-8227" data-mp="1yiw3ttodhtmn" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Power Ups */}
            {activeNav === 'power-ups' && (() => {
              const POWER_UPS = [{
                name: 'Journey Manager',
                desc: 'Uses the Giga Primitives (Journeys, integrations, pulses) to be an extremely...',
                tag: 'on journey',
                icon: LayoutTemplate
              }, {
                name: 'Process Emails',
                desc: 'Archives non-human emails',
                tag: 'Email',
                icon: LayoutTemplate
              }, {
                name: 'Process Meeting Notes',
                desc: 'Meeting Note Taker',
                tag: 'Meeting Note Taker',
                icon: LayoutTemplate
              }, {
                name: 'Product Backlog Manager',
                desc: 'Manages your product backlog',
                tag: 'on journey',
                icon: LayoutTemplate
              }];
              return <div className="flex flex-col" data-id="element-8228" data-mp="3sw7dzp4oy6oe">
                  <div className="flex items-center gap-2 mb-2" data-id="element-8229" data-mp="3sve4whi9f533">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8230" data-mp="bols3e070c9m">
                      Power Ups
                    </h1>
                    <span className="text-sm text-neutral-400 font-medium" data-id="element-8231" data-mp="6qagaxqc7ror">
                      {POWER_UPS.length}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 leading-relaxed mb-6 max-w-xl" data-id="element-8232" data-mp="3a67t7efvvdqs">
                    Ready-made automations. Pick a power up and we'll set it up with you.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8233" data-mp="3k3c58lg0lstv">
                    {POWER_UPS.map((p, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all bg-white" data-id="element-8234" data-mp="2wd9gofbb7500" data-mpi={i}>
                        <div className="flex items-start gap-4 mb-4" data-id="element-8235" data-mp="16xfl7scofc90">
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center text-neutral-400 shrink-0" data-id="element-8236" data-mp="16v2tinh49nir">
                            <p.icon size={20} data-id="element-8237" data-mp="x018o3tvj7lz" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1" data-id="element-8238" data-mp="11xjdbi0uz0o6">
                            <h3 className="font-semibold text-neutral-900 text-sm mb-1 leading-snug" data-id="element-8239" data-mp="1pnnihttp38ha">
                              {p.name}
                            </h3>
                            <p className="text-xs text-neutral-500 leading-relaxed" data-id="element-8240" data-mp="1ulyr61897cxx">
                              {p.desc}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-5 mt-auto" data-id="element-8241" data-mp="1kqf13vgdf8h3">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full" data-id="element-8242" data-mp="56ilivtjg9cs">
                            {p.tag === 'Email' ? <Paperclip size={10} className="mr-0.5" data-id="element-8243" data-mp="34e9c4ptcxprg" /> : null}
                            {p.tag}
                          </span>
                        </div>

                        <button className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8244" data-mp="3dfhb7a3p6uml">
                          <Download size={16} data-id="element-8245" data-mp="2lpcj0p9p53at" />
                          Install
                        </button>
                      </div>)}
                  </div>
                </div>;
            })()}

            {/* Groups — the access boundary for tracks, files and tables */}
            {activeNav === 'groups' && <GroupsView data-id="element-8246" data-mp="2zgzp1gtfxq6s" />}

            {/* Tracks */}
            {activeNav === 'spaces-tracks' && <TracksView data-id="element-8247" data-mp="221c3q74a02mi" />}

            {/* Tables */}
            {activeNav === 'tables' && <TablesView data-id="element-8248" data-mp="26zdmeqxshzxg" />}


            {/* Files */}
            {activeNav === 'files' && <div className="flex flex-col" data-id="element-8249" data-mp="26xaolkqeb2i6">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8250" data-mp="1zf30spwj78p3">
                  <div data-id="element-8251" data-mp="1ugjeuphxtwwq">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8252" data-mp="1uiclw5wgqj59">
                      Files
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8253" data-mp="1pg56158iiixn">
                      A shared workspace for documents — the files you upload
                      for Giga to work from, and the reports, exports, and
                      deliverables it creates for you.
                    </p>
                  </div>
                  <button className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8254" data-mp="16qxfa0xnlfo7">
                    <Plus size={16} data-id="element-8255" data-mp="xqipopdnpeew" />
                    Upload
                  </button>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8256" data-mp="sr5vtkodb72t">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wide text-neutral-400" data-id="element-8257" data-mp="j4blnhy8tbfd">
                    <span data-id="element-8258" data-mp="95vwhkis0c8i">Name</span>
                    <span className="hidden sm:block w-20 text-right" data-id="element-8259" data-mp="96xgwflomb2w">
                      Size
                    </span>
                    <span className="w-28 text-right" data-id="element-8260" data-mp="e58saurmktn8">Modified</span>
                  </div>
                  <div className="divide-y divide-neutral-100" data-id="element-8261" data-mp="11v355cby2i7h">
                    {[{
                    name: 'Reports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 14'
                  }, {
                    name: 'Exports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 12'
                  }, {
                    name: 'meeting-notes-jun-12.pdf',
                    type: 'file',
                    size: '184 KB',
                    modified: 'Jun 12'
                  }, {
                    name: 'q3-forecast.xlsx',
                    type: 'file',
                    size: '52 KB',
                    modified: 'Jun 10'
                  }, {
                    name: 'brand-guidelines.pdf',
                    type: 'file',
                    size: '3.1 MB',
                    modified: 'Jun 8'
                  }, {
                    name: 'invoices-184.zip',
                    type: 'file',
                    size: '8.4 MB',
                    modified: 'Jun 6'
                  }].map((f, i) => <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3.5 items-center hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8262" data-mp="2meye6h8t730p" data-mpi={i}>
                        <div className="flex items-center gap-3 min-w-0" data-id="element-8263" data-mp="2vbqoiso0rprd">
                          <div className="w-8 h-8 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8264" data-mp="flcr7qvibdhb">
                            {f.type === 'folder' ? <Folder size={16} className="text-neutral-400 fill-neutral-200" data-id="element-8265" data-mp="15x99v94gxmsb" /> : <FileText size={16} className="text-neutral-400" data-id="element-8266" data-mp="15z2i2suqcf2f" />}
                          </div>
                          <span className="text-sm font-medium text-neutral-900 truncate" data-id="element-8267" data-mp="1ay5lk8v2ad8a">
                            {f.name}
                          </span>
                        </div>
                        <span className="hidden sm:block w-20 text-right text-xs text-neutral-500" data-id="element-8268" data-mp="1ksfd5oc6j7hy">
                          {f.size}
                        </span>
                        <span className="w-28 text-right text-xs text-neutral-400" data-id="element-8269" data-mp="1tu57b1xg4kjf">
                          {f.modified}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Viewer */}
            {activeNav === 'viewer' && <div className="flex flex-col h-full" data-id="element-8270" data-mp="28l499u3s0au1">
                <div className="mb-6" data-id="element-8271" data-mp="2clo6sys9hggp">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8272" data-mp="2cmpuq3udlxfu">
                    Viewer
                  </h1>
                  <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8273" data-mp="zw1adf4x5so">
                    A focused, full-screen preview pane. Open any file or
                    conversation here to read it in detail without leaving the
                    dashboard.
                  </p>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center min-h-[320px] text-center border border-dashed border-neutral-200 rounded-xl" data-id="element-8274" data-mp="3j622qll70cge">
                  <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-8275" data-mp="foa4wqkdkyaa">
                    <Monitor size={26} className="text-neutral-400" data-id="element-8276" data-mp="av7hl066yl2j" />
                  </div>
                  <h2 className="text-lg font-bold text-neutral-900 mb-2" data-id="element-8277" data-mp="5wmhr1uuq3em">
                    Nothing to preview yet
                  </h2>
                  <p className="text-neutral-500 max-w-sm text-sm leading-relaxed" data-id="element-8278" data-mp="13emleptos3ch">
                    Select a file or conversation and it will open here for a
                    full-screen preview.
                  </p>
                </div>
              </div>}
          </div>
        </main>
      </div>

      {/* Right Chat Panel — persistent in "current" and "progressive"; in the
                                                                                                           calm-deferred and chat-first directions it stays hidden until the user
                                                                                                           explicitly enters a task (chatPanelOpen). In "progressive" it also stays
                                                                                                           hidden during first-time setup so a brand-new user isn't greeted by a
                                                                                                           chat — it appears once they finish setup, switch to the returning
                                                                                                           dashboard, or explicitly start a task (chatPanelOpen). */}
      {(variant === 'current' || variant === 'progressive' && progressiveState === 'returning' || chatPanelOpen) && <div className="w-[320px] lg:w-[380px] bg-white border-l border-neutral-200 flex flex-col shrink-0 h-screen hidden md:flex" data-id="element-8279" data-mp="mat25zjyl7yt">
          {/* Chat Tabs */}
          <div className="h-14 flex items-end gap-px px-2 border-b border-neutral-200 bg-neutral-50/50 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-8280" data-mp="w6wchsoyscwu">
            {chatTabs.map(tab => {
            const isActive = tab.id === activeChatId;
            return <div key={tab.id} onClick={() => {
              setActiveChatId(tab.id);
              setActivePlan(null);
            }} className={`shrink-0 px-4 py-2 flex items-center gap-3 text-sm font-medium cursor-pointer transition-colors ${isActive ? 'bg-white border border-neutral-200 border-b-0 rounded-t-lg text-neutral-900 relative top-px' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8281" data-mp="155rk997npv29">
                  {tab.label}
                  <button onClick={e => {
                e.stopPropagation();
                closeChat(tab.id);
              }} aria-label={`Close ${tab.label}`} className="text-neutral-400 hover:text-neutral-600" data-id="element-8282" data-mp="bplzeni3kj6">
                    <X size={14} data-id="element-8283" data-mp="93j61b28nddi" />
                  </button>
                </div>;
          })}
            <button onClick={openNewChat} aria-label="New chat" className="shrink-0 ml-1 mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-8284" data-mp="38kzkdwmfix9q">
              <Plus size={15} data-id="element-8285" data-mp="3hmqpp0d25232" />
            </button>
            {/* In the deferred directions, let the user collapse the panel back
             out of view so chat truly stays out of the way until summoned. */}
            {(variant === 'calm-deferred' || variant === 'chat-first') && <button onClick={() => setChatPanelOpen(false)} aria-label="Hide chat panel" title="Hide chat" className="shrink-0 ml-auto mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-8286" data-mp="11s6tndagmeu8">
                <ChevronRight size={16} data-id="element-8287" data-mp="126u2liyqs504" />
              </button>}
          </div>

          {/* Chat Content Area */}
          <div className="flex-1 p-4 flex flex-col overflow-y-auto" data-id="element-8288" data-mp="y58su6hi86u5">
            {chatTabs.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-id="element-8289" data-mp="t7qfm3wgf5gc">
                <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4" data-id="element-8290" data-mp="o72qjnrscyor">
                  <MessageSquare size={24} className="text-neutral-400" data-id="element-8291" data-mp="j99liy27yaq2" />
                </div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1" data-id="element-8292" data-mp="f8h95vy1vppk">
                  No active chats
                </h3>
                <p className="text-xs text-neutral-500 leading-relaxed max-w-[15rem] mb-5" data-id="element-8293" data-mp="2dkfz5p6xl8vo">
                  You closed your open chats. Start a new one to ask Giga to get
                  something done.
                </p>
                <button onClick={openNewChat} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8294" data-mp="3heuf2llbxlwe">
                  <Plus size={16} data-id="element-8295" data-mp="3qgaoaq0erhz1" />
                  New chat
                </button>
              </div> : activePlan ? <PlanView prompt={activePlan} onBack={() => setActivePlan(null)} data-id="element-8296" data-mp="2tqb3nx6zgt03" /> : <>
                <div className="flex items-center justify-between mb-6" data-id="element-8297" data-mp="2yocvp7me02np">
                  <ChatContextControls data-id="element-8298" data-mp="2ym8iu06iftba" />
                  <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8299" data-mp="32py4agrw1us3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8300" data-mp="37h6f9h8v9o7d">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-8301" data-mp="2f0lzru7iaaw4"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-8302" data-mp="2eyiz8g8q8zd5"></path>
                    </svg>
                  </button>
                </div>

                {/* Get moving — starter prompts based on connected tools */}
                <div className="flex items-center gap-2 mb-1.5" data-id="element-8303" data-mp="1yfn0x0oohnbx">
                  <Sparkles size={15} className="text-[#EA580C]" data-id="element-8304" data-mp="1thbpwmmnd6s4" />
                  <h3 className="text-sm font-semibold text-[#2B211A]" data-id="element-8305" data-mp="1tgtnt9dazyca">
                    Not sure where to start?
                  </h3>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-8306" data-mp="1jra11smgzkc5">
                  Here are a few things you can ask, based on the tools you’ve
                  connected.
                </p>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-8307" data-mp="1asolm6caolfl">
                  {STARTER_CATEGORIES.map(cat => {
                const isActive = starterCat === cat.id;
                return <button key={cat.id} onClick={() => setStarterCat(cat.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-[#2B211A] text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8308" data-mp="10tz3ccghkyyt">
                        {cat.label}
                      </button>;
              })}
                </div>

                {/* Prompt suggestions */}
                <div className="flex flex-col gap-2.5" data-id="element-8309" data-mp="m6qmow4es1fg">
                  {STARTER_PROMPTS[starterCat].map((prompt, i) => <button key={i} onClick={() => setActivePlan(prompt)} className="group text-left p-3.5 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8310" data-mp="19w1rqpsskhze" data-mpi={i}>
                      <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-8311" data-mp="105gk6cv5434c">
                        <span className="text-sm font-semibold text-[#2B211A]" data-id="element-8312" data-mp="1538ns455ddod">
                          {prompt.title}
                        </span>
                        <div className="flex items-center -space-x-1.5 shrink-0" data-id="element-8313" data-mp="1xkuh23ewrrl1">
                          {prompt.tools.slice(0, 3).map((tool, t) => <span key={t} className="w-5 h-5 rounded-md bg-white border border-neutral-200 flex items-center justify-center" data-id="element-8314" data-mp="3iahpuw2la9wf" data-mpi={t}>
                              <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3 h-3 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-8315" data-mp="3dac1borm93ik" />
                            </span>)}
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 leading-relaxed mb-2.5" data-id="element-8316" data-mp="2ufosyh8e4noa">
                        {prompt.desc}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#EA580C] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8317" data-mp="34bi9ebp8d1bc">
                        Try it
                        <ArrowRight size={12} data-id="element-8318" data-mp="jmar6wf3pvr3" />
                      </span>
                    </button>)}
                </div>
              </>}
          </div>

          {/* Chat Input — hidden when there are no open chats */}
          {chatTabs.length > 0 && <div className="p-4 border-t border-neutral-200 bg-white" data-id="element-8319" data-mp="1ixkodlr1njto">
              <div className="flex items-end gap-2" data-id="element-8320" data-mp="1nvm9ec815t7s">
                <button className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-8321" data-mp="1svh1gj3jkouf">
                  <Paperclip size={20} data-id="element-8322" data-mp="1xv3earfgk32p" />
                </button>
                <div className="flex-1 relative" data-id="element-8323" data-mp="1xsh6qrskhnkh">
                  <textarea placeholder="Enter your message" className="w-full border border-neutral-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none min-h-[44px] max-h-[120px]" rows={1} data-id="element-8324" data-mp="21vdm89mt93k7" />
                </div>
                <button className="p-2.5 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-8325" data-mp="2gll0se9p62fm">
                  <Send size={18} data-id="element-8326" data-mp="2ll7f6r19hoq0" />
                </button>
              </div>
            </div>}
        </div>}

      {/* Create-a-pulse slide-over */}
      <PulseCreatePanel open={pulseCreateOpen} onClose={() => setPulseCreateOpen(false)} data-id="element-8327" data-mp="9ug4ylmx0hm4" />

      {/* Create-a-journey slide-over */}
      <JourneyCreatePanel open={journeyCreateOpen} onClose={() => setJourneyCreateOpen(false)} data-id="element-8328" data-mp="3w3hf0xow0yni" />

      {/* Create-an-assistant slide-over */}
      <AssistantCreatePanel open={assistantCreateOpen} onClose={() => setAssistantCreateOpen(false)} data-id="element-8329" data-mp="3r6qxs71cjx0i" />

      {/* Persistent Onboarding Widget */}
      <OnboardingWidget onNavigate={setActiveNav} data-id="element-8330" data-mp="nnnvsb36992r" />

      {/* Persistent "Book a call" — always available bottom-right for anyone
                   who's stuck or hasn't set anything up yet. Sits above the debug
                   cycler so the two don't overlap. */}
      <a href="/talk-to-sales" className="fixed bottom-16 right-5 z-50 inline-flex items-center gap-2 h-11 pl-4 pr-5 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold shadow-xl transition-colors" data-id="element-8331" data-mp="dwjsi8muzcnr">
        <PhoneCall size={16} className="text-[#EA580C]" data-id="element-8332" data-mp="30v5k3limmmyo" />
        Book a call
      </a>

      {/* DEBUG: Variant cycler — one subtle button, bottom-right. Each tap
                                                                           advances to the next dashboard direction. A short label flashes on
                                                                           hover / right after a change so you know which one you're seeing,
                                                                           then fades away to stay out of the way. */}
      <VariantCycler variant={variant} setVariant={setVariant} data-id="element-8333" data-mp="2m3wrd3jfglhb" />
    </div>
  );
}
// One small, low-key control for flipping between dashboard directions while
// designing. Stays tucked in the corner; reveals the current variant's name on
// hover or for a beat after each tap, otherwise it's just a dot.
const DASHBOARD_VARIANTS: {
  id: DashboardVariant;
  label: string;
}[] = [{
  id: 'progressive',
  label: 'Progressive Disclosure'
}, {
  id: 'calm-deferred',
  label: 'Calm + Deferred Chat'
}, {
  id: 'chat-first',
  label: 'Chat-First'
}, {
  id: 'current',
  label: 'Current'
}];
function VariantCycler({
  variant,
  setVariant
}: {
  variant: DashboardVariant;
  setVariant: (v: DashboardVariant) => void;
}) {
  const [hovered, setHovered] = useState(false);
  // Briefly reveal the label right after a tap, then let it fade.
  const [justChanged, setJustChanged] = useState(false);
  const current = DASHBOARD_VARIANTS.find(v => v.id === variant);
  const cycle = () => {
    const idx = DASHBOARD_VARIANTS.findIndex(v => v.id === variant);
    const next = DASHBOARD_VARIANTS[(idx + 1) % DASHBOARD_VARIANTS.length];
    setVariant(next.id);
    setJustChanged(true);
    window.setTimeout(() => setJustChanged(false), 1400);
  };
  const showLabel = hovered || justChanged;
  return <button onClick={cycle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} title="Switch dashboard variant" aria-label={`Dashboard variant: ${current?.label}. Click to switch.`} className="fixed bottom-5 right-5 z-[9999] flex items-center gap-2 h-8 pl-2.5 pr-2.5 rounded-full bg-[#2B211A]/80 hover:bg-[#2B211A] text-white/70 hover:text-white backdrop-blur-sm border border-white/10 shadow-lg transition-all duration-300" data-id="element-8334" data-mp="u8r7iwmvlky8">
      <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0" data-id="element-8335" data-mp="i3kb211tc1v9" />
      <span className={`overflow-hidden whitespace-nowrap text-[11px] font-medium tracking-wide transition-all duration-300 ${showLabel ? 'max-w-[200px] opacity-100' : 'max-w-0 opacity-0'}`} data-id="element-8336" data-mp="3gkwnch87328b">
        {current?.label}
      </span>
    </button>;
}
// ── Shared Overview data ─────────────────────────────────────────────────────
// Reused across the dashboard direction variants so each mockup reads as the
// same product with the same underlying data — only the layout/hierarchy
// changes between directions.
const OVERVIEW_STATS = [{
  label: 'Tasks Completed',
  value: '14,231',
  note: '+12% this week',
  tone: 'good' as const
}, {
  label: 'Hours Saved',
  value: '342',
  note: '~14 days of work',
  tone: 'good' as const
}, {
  label: 'Active Automations',
  value: '12',
  note: 'Running smoothly',
  tone: 'neutral' as const
}];
const RECENT_ACTIVITY = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  tool: 'notion',
  time: '10 mins ago'
}, {
  task: 'Pulled account history from HubSpot',
  tool: 'hubspot',
  time: '12 mins ago'
}, {
  task: 'Replied to support ticket #4092',
  tool: 'zendesk',
  time: '1 hour ago'
}, {
  task: 'Summarized weekly sync meeting',
  tool: 'slack',
  time: '3 hours ago'
}, {
  task: 'Updated 14 stale deals in CRM',
  tool: 'salesforce',
  time: '5 hours ago'
}];
// Full activity log — the browsable, filterable record behind the Overview's
// "Recent Activity" teaser. Mixes chats, pulses, journeys, recipes, and
// assistants, with outcomes so failures surface.
const ACTIVITY_LOG: ActivityEntry[] = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  detail: 'Pulled the account history and assembled a one-page brief.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'notion',
  daysAgo: 0,
  time: '10 mins ago'
}, {
  task: 'Morning briefing delivered',
  detail: 'Calendar, unread mail, and tasks due today, sent at 8:00am.',
  type: 'Pulse',
  status: 'success',
  source: 'Daily Briefing',
  tool: 'gmail',
  daysAgo: 0,
  time: '12 mins ago'
}, {
  task: 'Enriching 6 new inbound leads',
  detail: 'Filling in firmographics and scoring against your ICP.',
  type: 'Recipe',
  status: 'running',
  source: 'Lead Enrichment',
  tool: 'hubspot',
  daysAgo: 0,
  time: '20 mins ago'
}, {
  task: 'Could not post recap to #leadership',
  detail: 'Slack returned a permissions error — reconnect may be needed.',
  type: 'Pulse',
  status: 'needs-attention',
  source: 'Weekly Report',
  tool: 'slack',
  daysAgo: 0,
  time: '1 hour ago'
}, {
  task: 'Replied to support ticket #4092',
  detail: 'Drafted from help docs and sent after your approval.',
  type: 'Assistant',
  status: 'success',
  source: 'Support Bot',
  tool: 'zendesk',
  daysAgo: 0,
  time: '2 hours ago'
}, {
  task: 'Checked in on “Early User Activation”',
  detail: 'Summarized progress since last week and proposed next steps.',
  type: 'Journey',
  status: 'success',
  source: 'Early User Activation',
  tool: 'notion',
  daysAgo: 0,
  time: '3 hours ago'
}, {
  task: 'Summarized weekly sync meeting',
  detail: 'Action items captured and assigned to owners.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'slack',
  daysAgo: 1,
  time: '4:12 PM'
}, {
  task: 'Opened GitHub issue for Sentry spike',
  detail: 'Bundled the stack trace and tagged the likely owner.',
  type: 'Recipe',
  status: 'success',
  source: 'Bug Triage',
  tool: 'github',
  daysAgo: 1,
  time: '1:48 PM'
}, {
  task: 'Updated 14 stale deals in CRM',
  detail: 'Refreshed stages and logged notes after each touchpoint.',
  type: 'Assistant',
  status: 'success',
  source: 'Sales Assistant',
  tool: 'salesforce',
  daysAgo: 1,
  time: '11:30 AM'
}, {
  task: 'Invoice chase paused — missing connection',
  detail: 'QuickBooks token expired before reminders went out.',
  type: 'Recipe',
  status: 'needs-attention',
  source: 'Invoice Chaser',
  tool: 'quickbooks',
  daysAgo: 1,
  time: '9:05 AM'
}, {
  task: 'Compiled the weekly leadership digest',
  detail: 'Pulled last week’s metrics and posted the summary to #leadership.',
  type: 'Recipe',
  status: 'success',
  source: 'Weekly Report',
  tool: 'notion',
  daysAgo: 3,
  time: 'Mon, 9:00 AM'
}, {
  task: 'Onboarded a new closed-won deal',
  detail: 'Spun up the client workspace and sent the welcome sequence.',
  type: 'Recipe',
  status: 'success',
  source: 'Deal Onboarding',
  tool: 'gmail',
  daysAgo: 4,
  time: 'Mon, 2:30 PM'
}, {
  task: 'Swept 9 stale Linear issues',
  detail: 'Nudged each owner with their list of issues idle 14+ days.',
  type: 'Pulse',
  status: 'success',
  source: 'Stale Issue Sweep',
  tool: 'linear',
  daysAgo: 5,
  time: 'Tue, 8:15 AM'
}, {
  task: 'Generated monthly marketing performance report',
  detail: 'Aggregated spend and conversion metrics across all channels.',
  type: 'Recipe',
  status: 'success',
  source: 'Monthly Marketing Report',
  tool: 'google',
  daysAgo: 12,
  time: '9:00 AM'
}, {
  task: 'Processed 42 invoices from Stripe',
  detail: 'Matched payments to open invoices and updated the ledger.',
  type: 'Assistant',
  status: 'success',
  source: 'Finance Assistant',
  tool: 'stripe',
  daysAgo: 28,
  time: '4:30 PM'
}, {
  task: 'Audited Meta Ads spend',
  detail: 'Paused 3 underperforming ad sets and exported the summary.',
  type: 'Recipe',
  status: 'success',
  source: 'Ad Spend Audit',
  tool: 'meta',
  daysAgo: 75,
  time: '10:15 AM'
}, {
  task: 'Set up new hire onboarding for Alex',
  detail: 'Created Notion workspace, requested tool access, and queued intros.',
  type: 'Journey',
  status: 'success',
  source: 'New Hire Onboarding',
  tool: 'notion',
  daysAgo: 210,
  time: '9:00 AM'
}];
const ACTIVITY_TYPE_FILTERS: (ActivityType | 'all')[] = ['all', 'Chat', 'Pulse', 'Journey', 'Recipe', 'Assistant'];
// Small status pill + icon, reused per activity row.
function ActivityStatusBadge({
  status
}: {
  status: ActivityStatus;
}) {
  if (status === 'running') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8337" data-mp="1pgui7u1hi9ou">
        <Loader2 size={11} className="animate-spin" data-id="element-8338" data-mp="1z878ih7mwrsr" />
        Running
      </span>;
  }
  if (status === 'needs-attention') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8339" data-mp="2d6ouoahw5w8k">
        <AlertTriangle size={11} data-id="element-8340" data-mp="294abfpab8ugr" />
        Needs attention
      </span>;
  }
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8341" data-mp="ityzzqjp8j04">
      <CheckCircle2 size={11} data-id="element-8342" data-mp="4of7skndrnzu" />
      Done
    </span>;
}
// The Activity view — a filterable, grouped audit feed of everything Giga did.
const RANGE_MAX_DAYS: Record<Exclude<ActivityRange, 'custom'>, number> = {
  all: Infinity,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '6m': 180,
  '12m': 365
};
function getBucketForDaysAgo(daysAgo: number): string {
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo <= 6) return 'This week';
  return 'Earlier';
}
function ActivityView({
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  rangeFilter,
  setRangeFilter
}: {
  typeFilter: ActivityType | 'all';
  setTypeFilter: (t: ActivityType | 'all') => void;
  statusFilter: ActivityStatus | 'all';
  setStatusFilter: (s: ActivityStatus | 'all') => void;
  rangeFilter: ActivityRange;
  setRangeFilter: (r: ActivityRange) => void;
}) {
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const filtered = ACTIVITY_LOG.filter(e => {
    if (typeFilter !== 'all' && e.type !== typeFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (rangeFilter === 'custom') {
      // In a real app, we'd parse dates. Here we just show everything if custom is selected but empty.
      // If dates are provided, we'd calculate daysAgo. For prototype, we'll just let it pass.
      return true;
    } else {
      const maxDays = RANGE_MAX_DAYS[rangeFilter];
      if (e.daysAgo > maxDays) return false;
    }
    return true;
  });
  const stats = {
    total: filtered.length,
    success: filtered.filter(e => e.status === 'success').length,
    running: filtered.filter(e => e.status === 'running').length,
    needsAttention: filtered.filter(e => e.status === 'needs-attention').length
  };
  const buckets = ['Today', 'Yesterday', 'This week', 'Earlier'];
  return <div className="flex flex-col" data-id="element-8343" data-mp="26l7834vew718">
      <div className="mb-6" data-id="element-8344" data-mp="26kxjnlq0m7xn">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8345" data-mp="26jmgno4e947l">Activity</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8346" data-mp="2dyet9r7pg94j">
          Everything Giga has done across your chats, pulses, journeys, recipes,
          and assistants — with the outcome of each run.
        </p>
      </div>

      {/* Filter-aware summary strip */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-8347" data-mp="2sv5tg2p7dov7">
        <div className="flex-1 min-w-[120px]" data-id="element-8348" data-mp="2wuy6ospjkj6w">
          <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1" data-id="element-8349" data-mp="31uszj1svniul">
            Total
          </div>
          <div className="text-2xl font-bold text-neutral-900" data-id="element-8350" data-mp="36l9behh1uweu">
            {stats.total}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8351" data-mp="3bjauh2eh6vqb">
          <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1" data-id="element-8352" data-mp="3gjp42ovq6dqg">
            Done
          </div>
          <div className="text-2xl font-bold text-emerald-700" data-id="element-8353" data-mp="3kksgf1ekdrmx">
            {stats.success}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8354" data-mp="9pjrgug1vbs2">
          <div className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1" data-id="element-8355" data-mp="9spem454c3l5">
            Running
          </div>
          <div className="text-2xl font-bold text-amber-700" data-id="element-8356" data-mp="jpkbkilqtvxb">
            {stats.running}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8357" data-mp="eqza6fur967a">
          <div className="text-xs font-medium text-rose-600 uppercase tracking-wide mb-1" data-id="element-8358" data-mp="eo4ut2qlmbkq">
            Needs Attention
          </div>
          <div className="text-2xl font-bold text-rose-700" data-id="element-8359" data-mp="3i7fwfovvy8bq">
            {stats.needsAttention}
          </div>
        </div>
      </div>

      {/* Filters: type tabs + status dropdown */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-id="element-8360" data-mp="3s4c823h2zaki">
        <div role="tablist" aria-label="Filter activity by type" className="flex flex-wrap gap-2" data-id="element-8361" data-mp="34f9sr29x5tt6">
          {ACTIVITY_TYPE_FILTERS.map(t => {
          const active = typeFilter === t;
          return <button key={t} role="tab" aria-selected={active} onClick={() => setTypeFilter(t)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8362" data-mp="2zec8mk5cmpp1">
                {t === 'all' ? 'All' : t}
              </button>;
        })}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-8363" data-mp="19zkr74ixx4wj">
          {/* Timing filter — slice the feed by when it happened. */}
          <div className="flex items-center gap-2" data-id="element-8364" data-mp="1xmsvbj4mu7wz">
            <div className="relative" data-id="element-8365" data-mp="22mfcg3nl08b2">
              <Clock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8366" data-mp="22lcn405wqdx9" />
              <select aria-label="Filter activity by time" value={rangeFilter} onChange={e => setRangeFilter(e.target.value as ActivityRange)} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-8367" data-mp="1tms9vej48wgf">
                <option value="all" data-id="element-8368" data-mp="siam6u76z7x2">All time</option>
                <option value="7d" data-id="element-8369" data-mp="xgc6tjk9nfan">Last 7 days</option>
                <option value="30d" data-id="element-8370" data-mp="xf113e79fxgw">Last 30 days</option>
                <option value="90d" data-id="element-8371" data-mp="ijbo25k1yuob">Last 90 days</option>
                <option value="6m" data-id="element-8372" data-mp="iitmquij9q9j">Last 6 months</option>
                <option value="12m" data-id="element-8373" data-mp="nhd9gx4yb62y">Last 12 months</option>
                <option value="custom" data-id="element-8374" data-mp="njzeoq4dd9i0">Custom range</option>
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8375" data-mp="9pgl946elgbu" />
            </div>
            {rangeFilter === 'custom' && <div className="flex items-center gap-2" data-id="element-8376" data-mp="3vzaqdjxz2yfj">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-8377" data-mp="4rf1si52fezu" />
                <span className="text-neutral-400 text-sm" data-id="element-8378" data-mp="3qydc3ig32ojb">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-8379" data-mp="389t1xli0glmh" />
              </div>}
          </div>
          {/* Status filter — slice by outcome. */}
          <div className="relative" data-id="element-8380" data-mp="3camrskf0kai0">
            <Filter size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8381" data-mp="3r9gps23wonny" />
            <select aria-label="Filter activity by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as ActivityStatus | 'all')} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-8382" data-mp="3mb5h3upckj7b">
              <option value="all" data-id="element-8383" data-mp="dutvruuafl6q">All statuses</option>
              <option value="success" data-id="element-8384" data-mp="8vzbeogo9oki">Done</option>
              <option value="running" data-id="element-8385" data-mp="8tcva0fpu0qo">Running</option>
              <option value="needs-attention" data-id="element-8386" data-mp="32o8j685e97x8">Needs attention</option>
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8387" data-mp="36oj02fuq5iqv" />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-8388" data-mp="3gfdmbk7z0icb">
          <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-8389" data-mp="3ld5d2awvpyex">
            <History size={18} className="text-neutral-400" data-id="element-8390" data-mp="3qb6yv3lowbu3" />
          </div>
          <p className="text-sm font-semibold text-neutral-700" data-id="element-8391" data-mp="3ufwquhd8969q">
            Nothing matches these filters
          </p>
          <p className="text-xs text-neutral-500 mt-1" data-id="element-8392" data-mp="37iupms7jm6t">
            Try a different type, time range, or status.
          </p>
        </div> : <div className="flex flex-col gap-6" data-id="element-8393" data-mp="24zf5uvagqu12">
          {buckets.map(bucket => {
        const items = filtered.filter(e => getBucketForDaysAgo(e.daysAgo) === bucket);
        if (items.length === 0) return null;
        return <div key={bucket} data-id="element-8394" data-mp="1sp5zxxwr2fa1">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-neutral-400 mb-2" data-id="element-8395" data-mp="1snuvdvvhd3hv">
                  {bucket}
                </h2>
                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8396" data-mp="1irjgcy4i9zjq">
                  <div className="divide-y divide-neutral-100" data-id="element-8397" data-mp="2gerz4ld4gui1">
                    {items.map((e, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8398" data-mp="2bfxhhmqw5bzi" data-mpi={i}>
                        <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8399" data-mp="26ft0lvm2mf8r">
                          <img src={`https://cdn.simpleicons.org/${e.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={ev => {
                    ;
                    (ev.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                  }} data-id="element-8400" data-mp="22hlioxfvf7vz" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1" data-id="element-8401" data-mp="2jn8g2sk2fo5x">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-8402" data-mp="2jpbdhxnjsjko">
                            <span className="font-semibold text-neutral-900 text-sm" data-id="element-8403" data-mp="250f868cvlknr">
                              {e.task}
                            </span>
                            <ActivityStatusBadge status={e.status} data-id="element-8404" data-mp="2xmzyd5hm7qpq" />
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mt-0.5" data-id="element-8405" data-mp="32mmb7dtj74y0">
                            {e.detail}
                          </p>
                          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-neutral-400" data-id="element-8406" data-mp="2ooxpy4vjxvsf">
                            <span className="inline-flex items-center gap-1 font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full" data-id="element-8407" data-mp="2sq132jm7tdpy">
                              {e.type}
                            </span>
                            <span data-id="element-8408" data-mp="19oz7hudi4565">·</span>
                            <span className="truncate" data-id="element-8409" data-mp="19nxi0kvqng4w">{e.source}</span>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-8410" data-mp="14srwvoa5u5hv">
                          {e.time}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
}
const WHATS_NEW = [{
  tag: 'New',
  title: 'Pulses now support custom schedules',
  detail: 'Run recurring tasks on any cadence you define.'
}, {
  tag: 'Improved',
  title: 'Faster Slack + HubSpot sync',
  detail: 'Context updates land in under a second.'
}, {
  tag: 'New',
  title: 'Journeys keep context across check-ins',
  detail: 'Pick up long-running projects right where you left off.'
}];
function StatCard({
  label,
  value,
  note,
  tone
}: {
  label: string;
  value: string;
  note: string;
  tone: 'good' | 'neutral';
}) {
  return <div className="bg-white p-6 rounded-xl border border-neutral-200" data-id="element-8411" data-mp="sy3pihqyuvb8">
      <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-8412" data-mp="wz5qb30j7ffb">{label}</div>
      <div className="text-4xl font-bold text-neutral-900" data-id="element-8413" data-mp="wxcezaez470z">{value}</div>
      <div className={`mt-2 text-xs font-medium inline-flex px-2 py-0.5 rounded-full ${tone === 'good' ? 'text-[#0F9D58] bg-[#0F9D58]/10' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-8414" data-mp="11r6z2eyhqo0l">
        {note}
      </div>
    </div>;
}
function RecentActivityList() {
  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8415" data-mp="e0bcpxct0l0q">
      <div className="divide-y divide-neutral-100" data-id="element-8416" data-mp="e2e009mko01r">
        {RECENT_ACTIVITY.map((item, i) => <div key={i} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8417" data-mp="iy30qgabvmzn" data-mpi={i}>
            <div className="flex items-center gap-4" data-id="element-8418" data-mp="1ufcimydt66yu">
              <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8419" data-mp="1ugxdkp41brx8">
                <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" data-id="element-8420" data-mp="3jvydz8d2mjlx" />
              </div>
              <span className="font-medium text-neutral-900 text-sm" data-id="element-8421" data-mp="3exdkrsw8l06z">
                {item.task}
              </span>
            </div>
            <span className="text-xs font-medium text-neutral-400 sm:text-right" data-id="element-8422" data-mp="35725ja73r4ks">
              {item.time}
            </span>
          </div>)}
      </div>
    </div>;
}
// ── Direction: Current (unchanged baseline) ──────────────────────────────────
function CurrentOverview() {
  return <>
      <div className="flex items-center justify-between mb-8" data-id="element-8423" data-mp="2r716m0aee77y">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8424" data-mp="2r7j8bcftxdn9">Overview</h1>
        <div className="relative hidden md:block" data-id="element-8425" data-mp="2m8zopiopkdy2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-8426" data-mp="2maashii5llp6" />
          <input type="text" placeholder="Search activity..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-64" data-id="element-8427" data-mp="pda8z9182z7i" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10" data-id="element-8428" data-mp="3ued9bvcju5cp">
        {OVERVIEW_STATS.map(s => <StatCard key={s.label} {...s} data-id="element-8429" data-mp="3gni8958aksjo" />)}
      </div>
      <div data-id="element-8430" data-mp="3kpmydtzj771k">
        <h2 className="font-bold text-neutral-900 text-lg mb-4" data-id="element-8431" data-mp="3kpd59xj6wjrn">
          Recent Activity
        </h2>
        <RecentActivityList data-id="element-8432" data-mp="36pubyav1fcze" />
      </div>
    </>;
}
// ── Direction #1: Command Center ─────────────────────────────────────────────
// A state-aware dashboard for returning users. Surfaces failures/needs-attention
// items at the top, provides clickable stats routing to real destinations, and
// shows a cross-product heartbeat of what Giga has been doing.
function CommandCenterOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (nav: string) => void;
}) {
  // In a real app, this would be derived from the activity log or system state.
  // We hardcode a single failure here to demonstrate the "needs attention" state.
  const needsAttention = [{
    task: 'Could not post recap to #leadership',
    detail: 'Slack returned a permissions error — reconnect may be needed.',
    tool: 'slack'
  }];
  const reducedMotion = useReducedMotion();
  const hasAttention = needsAttention.length > 0;
  // "Happening now" — the tools Giga is actively touching, shown as a live
  // logo strip so you can SEE work in motion rather than read a list.
  const happeningNow = [{
    slug: 'hubspot',
    label: 'Enriching 6 leads',
    live: true
  }, {
    slug: 'gmail',
    label: 'Drafting 2 replies',
    live: true
  }, {
    slug: 'notion',
    label: 'Updating brief',
    live: false
  }, {
    slug: 'slack',
    label: 'Posting recap',
    live: false
  }];
  // Visual-only week rhythm: tasks completed per day, Mon→Sun.
  const weekRhythm = [{
    day: 'M',
    value: 42
  }, {
    day: 'T',
    value: 61
  }, {
    day: 'W',
    value: 38
  }, {
    day: 'T',
    value: 74
  }, {
    day: 'F',
    value: 88
  }, {
    day: 'S',
    value: 29
  }, {
    day: 'S',
    value: 18
  }];
  const peak = Math.max(...weekRhythm.map(d => d.value));
  // ── Tasks: work Giga has teed up that needs YOUR call ────────────────────
  // Distinct from "Recent activity" (what Giga already finished on its own).
  // Tasks are the handful of things Giga paused on — a draft to approve, a
  // charge to confirm, a reconnect — so the human stays in the loop. Each
  // routes to where it gets resolved.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review drafts',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  // ── Recent activity: a cross-product feed of finished work ───────────────
  const recentHeartbeat = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    type: 'Chat',
    tool: 'gmail',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    type: 'Journey',
    tool: 'hubspot',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    type: 'Pulse',
    tool: 'slack',
    status: 'done',
    time: '1h ago'
  }, {
    task: 'Updated the Q3 launch brief',
    type: 'Recipe',
    tool: 'notion',
    status: 'done',
    time: '3h ago'
  }, {
    task: 'Filed 8 receipts and reconciled 2 payments',
    type: 'Assistant',
    tool: 'stripe',
    status: 'done',
    time: 'Yesterday'
  }];
  const ease = (delay: number) => reducedMotion ? {
    duration: 0
  } : {
    duration: 0.45,
    delay,
    ease: 'easeOut' as const
  };
  return <div className="max-w-5xl mx-auto py-2" data-id="element-8433" data-mp="19qyxiyg5k007">
      {/* ── Hero status module ─────────────────────────────────────────────
                                         One strong visual anchor on a warm dark stage (the on-brand surface
                                         used across the marketing site), with a faint radial glow + dot grid.
                                         It folds the greeting and the system state into a single focal point
                                         and carries the primary "Start a task" action. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0)} className="relative overflow-hidden rounded-3xl bg-[#1A1410] px-6 py-8 md:px-10 md:py-10 mb-6" data-id="element-8434" data-mp="m038ua6ztkx6">
        {/* Faint warm glow — the only gradient allowed, on the dark stage. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        background: hasAttention ? 'radial-gradient(70% 60% at 80% 0%, rgba(225,29,72,0.18) 0%, transparent 70%)' : 'radial-gradient(70% 60% at 80% 0%, rgba(234,88,12,0.16) 0%, transparent 70%)'
      }} data-id="element-8435" data-mp="3l2ntkc0jf51r" />
        {/* Subtle dot grid texture. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        maskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)'
      }} data-id="element-8436" data-mp="2y2qk4jx01h6" />

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-7" data-id="element-8437" data-mp="2dnj3cn3y37mk">
          <div className="max-w-xl" data-id="element-8438" data-mp="2ipqh9i8c4xrj">
            <div className="flex items-center gap-2 mb-3" data-id="element-8439" data-mp="2yvblx3gy2f4s">
              <span className={`relative flex h-2 w-2 ${hasAttention ? '' : ''}`} data-id="element-8440" data-mp="2ywv5p41t78bb">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${reducedMotion ? '' : 'animate-ping'} ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-8441" data-mp="2u3tkkeb0ecja" />
                <span className={`relative inline-flex h-2 w-2 rounded-full ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-8442" data-mp="2k7gsfkv446qn" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-8443" data-mp="3hrtn0bhonrdp">
                {hasAttention ? `${needsAttention.length} item${needsAttention.length > 1 ? 's' : ''} need attention` : 'All systems healthy'}
              </span>
            </div>
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-8444" data-mp="32wdq7yppxhdm">
              Good morning, Faith
            </p>
            <h1 className="text-3xl md:text-[2.5rem] font-bold text-white tracking-tight leading-[1.08]" data-id="element-8445" data-mp="1weqkrnu8s9m6">
              {hasAttention ? 'One thing needs a quick fix.' : "You're all caught up."}
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-white/60" data-id="element-8446" data-mp="bysehtzne3qo">
              {hasAttention ? 'Everything else is running smoothly. Clear the item below and Giga keeps moving.' : 'Giga has been working in the background. Pick up where you left off or start something new.'}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-8447" data-mp="qnov4ffrsp2w">
              <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-white hover:bg-neutral-100 text-[#1A1410] px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8448" data-mp="qnf6av6gonys">
                <Sparkles size={16} className="text-[#EA580C]" data-id="element-8449" data-mp="zmabq51olu11" />
                Start a task
              </button>
              <button onClick={() => onNavigate('activity')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/75 hover:text-white transition-colors" data-id="element-8450" data-mp="14l3n9rxerayk">
                View all activity
                <ArrowRight size={15} data-id="element-8451" data-mp="2ydvbwpcyhpz0" />
              </button>
            </div>
          </div>

          {/* Hero side panel — either the focused needs-attention card, or a
           calm week-rhythm sparkline so the hero always carries a visual. */}
          <div className="w-full md:w-[300px] shrink-0" data-id="element-8452" data-mp="38dvxyj27mk6w">
            {hasAttention ? <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-8453" data-mp="33c6mj2b2s4ia">
                <div className="flex items-center gap-2 mb-3" data-id="element-8454" data-mp="3hbxxsva7j6sl">
                  <div className="w-7 h-7 rounded-lg bg-rose-500/20 border border-rose-400/30 flex items-center justify-center shrink-0" data-id="element-8455" data-mp="3dbnq9e6ro8bm">
                    <AlertTriangle size={14} className="text-rose-300" data-id="element-8456" data-mp="3r1hfhxm6oulv" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-rose-300" data-id="element-8457" data-mp="3m4r3bldi2j5q">
                    Needs attention
                  </span>
                </div>
                {needsAttention.map((item, i) => <div key={i} data-id="element-8458" data-mp="3w3gkfbxknzkx" data-mpi={i}>
                    <div className="flex items-center gap-2.5 mb-1.5" data-id="element-8459" data-mp="3w02ho2kzxocy">
                      <span className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shrink-0" data-id="element-8460" data-mp="jit1ywj49vac">
                        <img src={`https://cdn.simpleicons.org/${item.tool}/e11d48`} className="w-4 h-4 object-contain" alt="" data-id="element-8461" data-mp="nhsccwnmak96" />
                      </span>
                      <p className="text-sm font-semibold text-white leading-snug" data-id="element-8462" data-mp="eieylrsgn9in">
                        {item.task}
                      </p>
                    </div>
                    <p className="text-xs text-white/55 leading-relaxed mb-3" data-id="element-8463" data-mp="4ks7m6xvzrib">
                      {item.detail}
                    </p>
                    <button onClick={() => onNavigate('integrations')} className="w-full bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors" data-id="element-8464" data-mp="3n0b5irruwg4y">
                      Reconnect Slack
                    </button>
                  </div>)}
              </div> : <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-8465" data-mp="3hyvjawdy03kz">
                <div className="flex items-center justify-between mb-3" data-id="element-8466" data-mp="1bis3m251end">
                  <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-8467" data-mp="3tfthyf164nkx">
                    This week
                  </span>
                  <span className="text-xs font-semibold text-emerald-300" data-id="element-8468" data-mp="3off94urqt9lu">
                    350 tasks
                  </span>
                </div>
                <div className="flex items-end justify-between gap-1.5 h-20" data-id="element-8469" data-mp="3ejlu94qjx40w">
                  {weekRhythm.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-8470" data-mp="39s3ugk09l9hi" data-mpi={i}>
                      <motion.div initial={reducedMotion ? false : {
                  height: 0
                }} animate={{
                  height: `${d.value / peak * 100}%`
                }} transition={{
                  duration: reducedMotion ? 0 : 0.5,
                  delay: reducedMotion ? 0 : 0.25 + i * 0.05,
                  ease: 'easeOut'
                }} className={`w-full rounded-sm ${i === 4 ? 'bg-[#EA580C]' : 'bg-white/25'}`} style={{
                  minHeight: 4
                }} data-id="element-8471" data-mp="35q8xtsta3ka2" />
                      <span className="text-[10px] font-medium text-white/40" data-id="element-8472" data-mp="2lnmispldnb53">
                        {d.day}
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </motion.section>

      {/* ── Happening now ──────────────────────────────────────────────────
                                         A live tool strip — see Giga working, don't read about it. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.08)} className="mb-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-8473" data-mp="22w22alyiqt81">
        <div className="flex items-center gap-2 mb-4" data-id="element-8474" data-mp="1f93l1m2krh92">
          <Loader2 size={14} className={`text-[#EA580C] ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-8475" data-mp="1f6hg7u72jfuj" />
          <h2 className="text-sm font-semibold text-neutral-900" data-id="element-8476" data-mp="1k5k9khdorxmo">
            Happening now
          </h2>
          <span className="text-xs text-neutral-400" data-id="element-8477" data-mp="2khka47hfma8w">
            · {happeningNow.filter(h => h.live).length} active
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-8478" data-mp="2tj0n8mzmsqgt">
          {happeningNow.map((h, i) => <div key={i} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5" data-id="element-8479" data-mp="2yb1tnsa4bmyx" data-mpi={i}>
              <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8480" data-mp="339d3w44brzho">
                <img src={`https://cdn.simpleicons.org/${h.slug}/475569`} className="w-4.5 h-4.5 w-[18px] h-[18px] object-contain" alt="" onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
            }} data-id="element-8481" data-mp="3d8c4czexsotl" />
                {h.live && <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-id="element-8482" data-mp="1mvd7v48lotzp">
                    <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 ${reducedMotion ? '' : 'animate-ping'}`} data-id="element-8483" data-mp="1mxg5a9c31peg" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-8484" data-mp="1aklx4lu5ssp5" />
                  </span>}
              </span>
              <div className="min-w-0" data-id="element-8485" data-mp="11kz70w6vr9be">
                <p className="text-xs font-semibold text-neutral-900 truncate" data-id="element-8486" data-mp="11m0eyrslnfow">
                  {h.label}
                </p>
                <p className="text-[11px] text-neutral-400" data-id="element-8487" data-mp="1y8vn7zyeo2m3">
                  {h.live ? 'In progress' : 'Queued'}
                </p>
              </div>
            </div>)}
        </div>
      </motion.section>

      {/* ── Compact clickable stats ────────────────────────────────────────
                                         Supporting metrics, kept lean so they back up the hero rather than
                                         compete with it. Each routes to its real destination. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.16)} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-8488" data-mp="8uqmwyssaeu6">
        <button onClick={() => onNavigate('activity')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8489" data-mp="1cizi52u0n1m">
          <div className="flex items-center gap-2 mb-3" data-id="element-8490" data-mp="5fyvcz72ujd9">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-8491" data-mp="3nrbtcvf86qzj">
              <CheckCircle2 size={15} className="text-emerald-600" data-id="element-8492" data-mp="3sjlqowio6tbu" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8493" data-mp="3dupaudadgc0o">
              Tasks done
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-8494" data-mp="3ita8q625ijly" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8495" data-mp="39slvveohupan">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8496" data-mp="39u5cx7hz54dh">14,231</span>
            <span className="text-xs font-semibold text-emerald-700" data-id="element-8497" data-mp="2q6sqav2m6ui5">+12%</span>
          </div>
        </button>

        <button onClick={() => onNavigate('pulses')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8498" data-mp="2l9k7cs9n6mfu">
          <div className="flex items-center gap-2 mb-3" data-id="element-8499" data-mp="2v4c2lpfr4x7g">
            <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center" data-id="element-8500" data-mp="394mtgsh2absm">
              <Activity size={15} className="text-neutral-600" data-id="element-8501" data-mp="3473f7skd5z0o" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8502" data-mp="3iuy7j7ugm1d8">
              Active pulses
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-8503" data-mp="3dxg23rfni49z" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8504" data-mp="3mx2yfytiwjw6">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8505" data-mp="3mymh1w2nj714">12</span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500" data-id="element-8506" data-mp="1l6ug9bgw9zla">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-8507" data-mp="1q5fh9d3z0nas" />
              smooth
            </span>
          </div>
        </button>

        <div className="group relative p-5 rounded-2xl border border-neutral-200 bg-white" data-id="element-8508" data-mp="1uzt8d3blo89n">
          <div className="flex items-center gap-2 mb-3" data-id="element-8509" data-mp="1z0v7lk5iokbm">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-8510" data-mp="12a30vd4h44mi">
              <Clock size={15} className="text-amber-600" data-id="element-8511" data-mp="178w2o8aee76u" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8512" data-mp="1b8y6amzp1aor">
              Hours saved
            </span>
            <div className="relative ml-auto group/info" data-id="element-8513" data-mp="1ga424tonhe2i">
              <Info size={14} className="text-neutral-300 cursor-help hover:text-neutral-500 transition-colors" data-id="element-8514" data-mp="2mp4n64c50bub" />
              <div className="absolute bottom-full right-0 mb-2 w-52 p-2.5 bg-neutral-900 text-white text-xs rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity pointer-events-none z-10 leading-relaxed" data-id="element-8515" data-mp="2rmwnnmqul6a4">
                Estimated based on average time per task type across all
                connected tools.
              </div>
            </div>
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8516" data-mp="2o0hvt75kecbx">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8517" data-mp="2nzzp1gla0fpr">342</span>
            <span className="text-xs font-medium text-neutral-500" data-id="element-8518" data-mp="2j1y5kujxuedr">
              ~14 days
            </span>
          </div>
        </div>
      </motion.section>

      {/* ── Tasks ───────────────────────────────────────────────────────────
                                         Work Giga teed up that needs the user's call — approvals, confirms,
                                         and reconnects. Complements Recent activity (what's done) with what
                                         still needs a human. Falls back to a calm empty state when clear. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.22)} className="mb-6" data-id="element-8519" data-mp="1ws7vjk6mftsv">
        <div className="flex items-center justify-between mb-4" data-id="element-8520" data-mp="1ns1nwexlto1m">
          <div className="flex items-center gap-2.5" data-id="element-8521" data-mp="1ntw8fs4yre56">
            <h2 className="text-base font-semibold text-neutral-900" data-id="element-8522" data-mp="1rt4xijp26nue">Tasks</h2>
            {tasks.length > 0 && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-8523" data-mp="2fizcp7wuooht">
                {tasks.length}
              </span>}
          </div>
          <span className="text-xs font-medium text-neutral-400" data-id="element-8524" data-mp="26hre0oqf9ln7">
            Needs your review
          </span>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-8525" data-mp="2bft0ljn2433f">
          {tasks.length === 0 ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-8526" data-mp="102h0njuf44mj">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-8527" data-mp="1041t93x69dhr">
                <ListChecks size={18} className="text-neutral-400" data-id="element-8528" data-mp="nk4xj2bdtm4t" />
              </span>
              <div data-id="element-8529" data-mp="11jmewwmclx45">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-8530" data-mp="11k5ysivk6vkt">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-8531" data-mp="wluok71cqj22">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-8532" data-mp="ikrzdlf28smx">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-8533" data-mp="imuz4x60m04u" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-8534" data-mp="ds8lencgnvhl">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-8535" data-mp="2vrgwbpsqudkz" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-8536" data-mp="35obto5da67xo" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-8537" data-mp="3eps1q6gmhxyq">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-8538" data-mp="3jowch1j743r6">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-8539" data-mp="3ogmr3jp61doe">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-8540" data-mp="29pk5nd9prm8">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-8541" data-mp="1mpdz9qb2zkag" />
                  </button>
                </div>)}
            </div>}
        </div>
      </motion.section>

      {/* ── Recent timeline ────────────────────────────────────────────────
                                         A calmer, more visual rail of the latest work across the product. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.24)} data-id="element-8542" data-mp="25hghh63df8mt">
        <div className="flex items-center justify-between mb-4" data-id="element-8543" data-mp="20p5e1t9f94pi">
          <h2 className="text-base font-semibold text-neutral-900" data-id="element-8544" data-mp="1vsf1vh0qmt9d">
            Recent activity
          </h2>
          <button onClick={() => onNavigate('activity')} className="group flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-8545" data-mp="2t82meoi2wcsl">
            View all
            <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" data-id="element-8546" data-mp="3q5zfez73yi21" />
          </button>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-8547" data-mp="3l86ae9hjju3c">
          <div className="divide-y divide-neutral-100" data-id="element-8548" data-mp="2ydu32alzaxi">
            {recentHeartbeat.map((item, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors group/item cursor-pointer" data-id="element-8549" data-mp="3u5gkhrimhd6o" data-mpi={i}>
                <div className="relative flex items-center justify-center shrink-0 mt-0.5" data-id="element-8550" data-mp="7ztfivgp7jgf">
                  <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm transition-transform group-hover/item:scale-105" data-id="element-8551" data-mp="lqyia6fd4ruz">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                  ;
                  (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                }} data-id="element-8552" data-mp="hrqso7r9olhs" />
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-8553" data-mp="1aa06ssu7sups">
                    {item.status === 'running' ? <Loader2 size={9} className={`text-white ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-8554" data-mp="15byowb8iz1fw" /> : <Check size={9} className="text-white" data-id="element-8555" data-mp="rktmj1drvv1v" />}
                  </span>
                </div>

                <div className="flex-1 min-w-0" data-id="element-8556" data-mp="wcbnhpfspvmu">
                  <div className="flex items-start justify-between gap-3 mb-1.5" data-id="element-8557" data-mp="we6cpfy3o9wq">
                    <span className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-8558" data-mp="hoqkdvix7wex">
                      {item.task}
                    </span>
                    <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-8559" data-mp="8lz8xvq1p0na">
                      {item.time}
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-8560" data-mp="cpxbkgekwtl3">
                    {item.type}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </motion.section>

      {/* Mobile CTA */}
      <button onClick={onStartTask} className="sm:hidden mt-6 w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8561" data-mp="20mwo9e6drciq">
        <Sparkles size={16} data-id="element-8562" data-mp="2tb2c49sqdhms" />
        Start a task
      </button>
    </div>;
}
// ── Direction #2: Progressive disclosure based on state ──────────────────────
// First-ever load → a guided "get started" state. Once set up → the calm
// status overview. A sub-toggle lets you preview both moments.
function ProgressiveOverview({
  onConnect,
  onStartTask,
  onNavigate,
  state,
  onStateChange
}: {
  onConnect: () => void;
  onStartTask: () => void;
  onNavigate: (nav: string) => void; // first/returning is lifted to the parent Dashboard so the right chat panel
  // can stay hidden during first-time setup.
  state: 'first' | 'returning';
  onStateChange: (s: 'first' | 'returning') => void;
}) {
  const reducedMotion = useReducedMotion();
  const setState = onStateChange;
  // Track which step the user is currently on (1, 2, or 3)
  const [currentStep, setCurrentStep] = useState(1);
  // Set once all three steps are finished — shows the celebratory "all set"
  // payoff before the user drops into the everyday (returning) overview.
  const [allSet, setAllSet] = useState(false);
  const steps = [{
    id: 1,
    icon: Plug,
    title: 'Connect your tools',
    detail: 'Giga needs access to your apps to do work on your behalf.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8563" data-mp="2hvpgwe6ih736">
          <div className="flex flex-wrap gap-2" data-id="element-8564" data-mp="2hx0kodzyieua">
            {['slack', 'gmail', 'notion', 'linear', 'salesforce'].map(tool => <div key={tool} className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-8565" data-mp="2mw3qw1roar3u">
                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-5 h-5 object-contain" alt={tool} onError={e => {
            ;
            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
          }} data-id="element-8566" data-mp="1mlsmtr6a183t" />
                </div>)}
            <div className="w-10 h-10 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center border-dashed" data-id="element-8567" data-mp="18tcpn70244at">
              <span className="text-neutral-400 text-xs font-medium" data-id="element-8568" data-mp="13vujfodfc36i">+20</span>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8569" data-mp="1ikqmf70miq09">
            <button onClick={() => {
          onConnect();
          // Simulate completing the step after a short delay
          setTimeout(() => setCurrentStep(2), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8570" data-mp="1ds5ts8tgeiyb">
              Connect integrations
            </button>
            <button onClick={() => setCurrentStep(2)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8571" data-mp="2wpk9h1pnb3lv">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 2,
    icon: Sparkles,
    title: 'Try your first task',
    detail: 'See Giga in action. Ask it to summarize a thread or draft an email.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8572" data-mp="3fio5gtqc5sjn">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm" data-id="element-8573" data-mp="3khhjcn9jblkc">
            <p className="text-sm text-neutral-600 mb-3 font-medium" data-id="element-8574" data-mp="36hgiv8x6mg5e">
              Try asking:
            </p>
            <div className="flex flex-col gap-2" data-id="element-8575" data-mp="3ai0jimgys1wa">
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-8576" data-mp="3akv1m7ci9amj">
                "Summarize my unread emails from today"
              </button>
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-8577" data-mp="ml5emk9tazx">
                "What are my open tasks in Linear?"
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8578" data-mp="3e2sldv250o3e">
            <button onClick={() => {
          onStartTask();
          setTimeout(() => setCurrentStep(3), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8579" data-mp="390ti0l6edr6g">
              Start a new chat
            </button>
            <button onClick={() => setCurrentStep(3)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8580" data-mp="12c83ijxcknkr">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 3,
    icon: Activity,
    title: 'Set up a recurring pulse',
    detail: 'Automate your routine. Have Giga send a daily brief or weekly report.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8581" data-mp="1ojtz4if52qr4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-8582" data-mp="1olokfxubokvq">
            <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8583" data-mp="rvo2oisdf4nx">
              <Clock size={18} className="text-[#EA580C]" data-id="element-8584" data-mp="10tq7l9f86hgh" />
            </div>
            <div data-id="element-8585" data-mp="15st425h53fcu">
              <p className="text-sm font-semibold text-neutral-900" data-id="element-8586" data-mp="15tclll2vo1qc">
                Daily Morning Briefing
              </p>
              <p className="text-xs text-neutral-500" data-id="element-8587" data-mp="2c8dax7xedlny">
                Runs every weekday at 8:00 AM
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8588" data-mp="32c0peqlrwhod">
            <button onClick={() => setAllSet(true)} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8589" data-mp="32ctl8wj71mrq">
              Complete setup
            </button>
            <button onClick={() => setAllSet(true)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8590" data-mp="3c3y3q682vprb">
              Skip for now
            </button>
          </div>
        </div>
  }];
  const progressPercentage = (currentStep - 1) / steps.length * 100;
  return <div className="max-w-3xl mx-auto py-6" data-id="element-8591" data-mp="3h3j0qb3oto1e">
      <div className="flex justify-center mb-8" data-id="element-8592" data-mp="3v17j9caa8j3a">
        <div className="inline-flex p-1 rounded-full bg-neutral-100 border border-neutral-200" data-id="element-8593" data-mp="3uyv3n5rzykte">
          {(['first', 'returning'] as const).map(s => <button key={s} onClick={() => setState(s)} aria-pressed={state === s} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${state === s ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-8594" data-mp="20m50ryjni19v">
              {s === 'first' ? 'First-time' : 'Returning'}
            </button>)}
        </div>
      </div>

      {state === 'returning' ? <CommandCenterOverview onStartTask={onStartTask} onNavigate={onNavigate} data-id="element-8595" data-mp="2e0v3ua4i31m" /> : allSet ? <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="flex flex-col items-center text-center py-10" data-id="element-8596" data-mp="6euhukbtxbsx">
          <motion.span initial={reducedMotion ? false : {
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 18,
        delay: reducedMotion ? 0 : 0.05
      }} className="w-16 h-16 rounded-2xl bg-[#0F9D58] text-white flex items-center justify-center mb-6 shadow-sm" data-id="element-8597" data-mp="3thfr7i4vbev0">
            <CheckCircle2 size={32} data-id="element-8598" data-mp="1hrwux5epzj8c" />
          </motion.span>
          <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-2" data-id="element-8599" data-mp="1zkbjv2g5fyg">
            You're all set, Faith
          </h1>
          <p className="text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-8600" data-mp="6tokukz6q02p">
            Giga is connected and ready. Ask it to get something done — or let
            it keep working in the background.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3" data-id="element-8601" data-mp="krmujd2k98bv">
            <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8602" data-mp="kpjvk3jfk4v0">
              <Sparkles size={16} data-id="element-8603" data-mp="avjhvda31rao" />
              Start a task
            </button>
            <button onClick={() => setState('returning')} className="px-6 py-3 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-8604" data-mp="fqyy2bvkbza9">
              Go to dashboard
            </button>
          </div>
        </motion.div> : <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.45,
      ease: 'easeOut'
    }} data-id="element-8605" data-mp="1ikw21qpmqb4h">
          <div className="mb-10" data-id="element-8606" data-mp="1rkstu17bfq3r">
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-8607" data-mp="1rjq2js62zlnb">
              Welcome, Faith
            </p>
            <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-8608" data-mp="1nie7360j0677">
              Let's get Giga working for you
            </h1>
            <p className="text-lg text-neutral-500 leading-relaxed max-w-xl" data-id="element-8609" data-mp="uwmh0r08pree">
              Setup takes about 2 minutes. Once you&rsquo;re connected,
              here&rsquo;s what you can put Giga to work on:
            </p>

            {/* What you can set up — concrete product surfaces, not a pitch */}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-8610" data-mp="g5ch0tysviag">
              {[{
            icon: MessageSquare,
            label: 'Ask in chat'
          }, {
            icon: LayoutTemplate,
            label: 'Run recipes'
          }, {
            icon: Activity,
            label: 'Schedule pulses'
          }, {
            icon: Map,
            label: 'Track journeys'
          }, {
            icon: UserCircle,
            label: 'Build assistants'
          }].map(c => <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700" data-id="element-8611" data-mp="2pz9de43fqbo0">
                  <c.icon size={14} className="text-[#EA580C]" data-id="element-8612" data-mp="1x9sqtn2hxozp" />
                  {c.label}
                </span>)}
            </div>

            {/* Progress Bar */}
            <div className="mt-8 flex items-center gap-4" data-id="element-8613" data-mp="227ubudjhfydt">
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden" data-id="element-8614" data-mp="278i7jcinp3ed">
                <div className="h-full bg-[#EA580C] transition-all duration-500 ease-out rounded-full" style={{
              width: `${progressPercentage}%`
            }} data-id="element-8615" data-mp="3dou2l4q6m55q" />
              </div>
              <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider" data-id="element-8616" data-mp="170jw9i19svci">
                {currentStep - 1} of {steps.length} done
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-8617" data-mp="x9pbyj7l45tp">
            {steps.map(step => {
          const isPast = step.id < currentStep;
          const isActive = step.id === currentStep;
          const isFuture = step.id > currentStep;
          return <div key={step.id} className={`relative rounded-2xl border transition-all duration-300 overflow-hidden ${isActive ? 'border-[#EA580C] bg-white shadow-md ring-1 ring-[#EA580C]/10' : isPast ? 'border-neutral-200 bg-neutral-50/50' : 'border-neutral-200 bg-white opacity-60'}`} data-id="element-8618" data-mp="ndm7h6p9fv3l">
                  <div className={`px-6 py-5 flex items-start gap-4 ${isActive ? '' : 'items-center'}`} data-id="element-8619" data-mp="9dl49kliwbky">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isPast ? 'bg-[#0F9D58] text-white' : isActive ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-8620" data-mp="28ovtbncade9x">
                      {isPast ? <CheckCircle2 size={24} data-id="element-8621" data-mp="15e89lg761rlz" /> : <step.icon size={24} data-id="element-8622" data-mp="15dgioioiki21" />}
                    </div>

                    <div className="flex-1 min-w-0 pt-1" data-id="element-8623" data-mp="rf8htzocwwll">
                      <div className="flex items-center justify-between gap-4" data-id="element-8624" data-mp="rgtcrqel2hjz">
                        <h3 className={`text-base font-semibold ${isPast ? 'text-neutral-500 line-through decoration-neutral-300' : 'text-neutral-900'}`} data-id="element-8625" data-mp="wc8mq79gztb4">
                          {step.title}
                        </h3>
                        {isPast && <button onClick={() => setCurrentStep(step.id)} className="text-xs font-medium text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8626" data-mp="mhgnkz08oye8">
                            Revisit
                          </button>}
                      </div>

                      {(!isPast || isActive) && <p className={`text-sm mt-1 ${isActive ? 'text-neutral-600' : 'text-neutral-400'}`} data-id="element-8627" data-mp="3uxii592fjl82">
                          {step.detail}
                        </p>}

                      {/* Expandable Content for Active Step */}
                      <div className={`grid transition-all duration-300 ease-in-out ${isActive ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-8628" data-mp="3c7wz9eatd7wo">
                        <div className="overflow-hidden" data-id="element-8629" data-mp="3q4k03s5w0i7v">{step.content}</div>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {/* Global escape hatch — never trap a new user in the flow */}
          <div className="mt-6 text-center" data-id="element-8630" data-mp="3v5r5jkkk55bd">
            <button onClick={() => setState('returning')} className="text-sm font-medium text-neutral-400 hover:text-neutral-700 transition-colors" data-id="element-8631" data-mp="crqizhxh6w73">
              Skip setup — I'll explore on my own
            </button>
          </div>
        </motion.div>}
    </div>;
}
// ── Direction #3: Chat-first, everything else collapses ──────────────────────
// Leans fully into chat as the one surface: a big composer takes center stage,
// stats demote to a slim chip row beneath it.
function ChatFirstOverview({
  onStartTask
}: {
  onStartTask: () => void;
}) {
  return <div className="flex flex-col h-full min-h-[480px]" data-id="element-8632" data-mp="ttnt9ma5pilz">
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-id="element-8633" data-mp="ys7gdq0hl0fx">
        <span className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center mb-6" data-id="element-8634" data-mp="13jh0yc76hdky">
          <Sparkles size={26} className="text-white" data-id="element-8635" data-mp="18j212py33rz9" />
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-8636" data-mp="1cjvozjbj16s5">
          What can I help you with?
        </h1>
        <p className="text-neutral-500 mb-8 max-w-md" data-id="element-8637" data-mp="1hk08li7qehml">
          Describe a task and Giga will get it done across your connected tools.
        </p>
        <div className="w-full max-w-2xl" data-id="element-8638" data-mp="1mfp85ljr3yiw">
          <div className="relative rounded-2xl border border-neutral-200 bg-white shadow-sm focus-within:border-[#EA580C] focus-within:ring-1 focus-within:ring-[#EA580C] transition-colors" data-id="element-8639" data-mp="3jb4stt7c4fww">
            <textarea placeholder="Ask Giga to draft, summarize, update, or automate…" rows={3} className="w-full bg-transparent rounded-2xl px-5 py-4 pr-14 text-sm resize-none focus:outline-none" data-id="element-8640" data-mp="3gq5iwmrl63z9" />
            <button onClick={onStartTask} aria-label="Send" className="absolute right-3 bottom-3 w-9 h-9 rounded-xl bg-[#2B211A] hover:bg-[#1A1410] text-white flex items-center justify-center transition-colors" data-id="element-8641" data-mp="36zavvg6in0cr">
              <Send size={16} data-id="element-8642" data-mp="3ic8ox7s2vu2" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 pt-8" data-id="element-8643" data-mp="3upez3mfsky38">
        {OVERVIEW_STATS.map(s => <span key={s.label} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs" data-id="element-8644" data-mp="3ppk8ljzxiaip">
            <span className="font-bold text-neutral-900" data-id="element-8645" data-mp="3ktlgblbhc8d0">{s.value}</span>
            <span className="text-neutral-500" data-id="element-8646" data-mp="2eczx8b2gz9il">{s.label}</span>
          </span>)}
      </div>
    </div>;
}
// ── Create-a-pulse slide-over panel ──────────────────────────────────────────
// A right-anchored sheet for composing a new recurring pulse: name + prompt,
// a "Saved time" vs "Custom" run mode, a list of selectable saved times, a
// destination note, and an advanced-settings disclosure. Slides in from the
// right over a dimmed backdrop, mirroring the product's real pulse builder.
type SavedTime = {
  id: string;
  label: string;
  cadence: string;
};
const PULSE_SAVED_TIMES: SavedTime[] = [{
  id: 'morning',
  label: 'Morning',
  cadence: 'Daily at 08:00 Etc/UTC'
}, {
  id: 'afternoon',
  label: 'Afternoon',
  cadence: 'Daily at 13:00 Etc/UTC'
}, {
  id: 'evening',
  label: 'Evening',
  cadence: 'Daily at 18:00 Etc/UTC'
}, {
  id: 'weekly',
  label: 'Weekly',
  cadence: 'Mondays at 09:00 Etc/UTC'
}, {
  id: 'monthly',
  label: 'Monthly',
  cadence: 'Monthly (day 1) at 09:00 Etc/UTC'
}];
function PulseCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [runMode, setRunMode] = useState<'saved' | 'custom'>('saved');
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const toggleTime = (id: string) => setSelectedTimes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  return <AnimatePresence data-id="element-8647" data-mp="1g1jnfwr2719n">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-8648" data-mp="1g08dff70sxa3">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-8649" data-mp="2wehyo0fyu5wp" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a pulse" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-8650" data-mp="313omr14w53wh">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-8651" data-mp="3jx0vml1pey5g">
              <div data-id="element-8652" data-mp="3f1v0qwqbq958">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-8653" data-mp="3b2w1nsr9x6lp">
                  New pulse
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-8654" data-mp="bgfiqt5ybkx2">
                  Create a pulse
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-8655" data-mp="1pbflx2jd6y1d">
                <X size={20} data-id="element-8656" data-mp="1uc3jk723md4k" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-8657" data-mp="23axcnng80ldd">
              {/* Name */}
              <div data-id="element-8658" data-mp="28a1lgcz8gh36">
                <label htmlFor="pulse-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8659" data-mp="28ajrg1bp69oa">
                  Name
                </label>
                <input id="pulse-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Morning briefing" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-8660" data-mp="2d7a2294qgd2b" />
              </div>

              {/* Prompt */}
              <div data-id="element-8661" data-mp="2w047abck0qtc">
                <label htmlFor="pulse-prompt" className="block text-sm font-semibold text-neutral-900" data-id="element-8662" data-mp="2vxhzqbpnybb4">
                  Prompt
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8663" data-mp="qzbcwrqobkk5">
                  What the assistant should do when this pulse runs.
                </p>
                <textarea id="pulse-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Summarise my unread messages and flag anything urgent…" rows={5} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-8664" data-mp="10wfzutscfk20" />
              </div>

              {/* When should it run? */}
              <div data-id="element-8665" data-mp="1jo8sgn3i3397">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3" data-id="element-8666" data-mp="1jnqmgyr1dao3">
                  When should it run?
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-8667" data-mp="3lek7w2npgm40">
                  <button type="button" onClick={() => setRunMode('saved')} aria-pressed={runMode === 'saved'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'saved' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-8668" data-mp="3lg54ryxhsh51">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'saved' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-8669" data-mp="1r4dqmz04j5y3">
                      <Clock size={18} data-id="element-8670" data-mp="1i4h0esy35z0x" />
                    </span>
                    <span className="min-w-0" data-id="element-8671" data-mp="1n5wgs7pbjhcz">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-8672" data-mp="1n2ihj8bybo9r">
                        Saved time
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-8673" data-mp="1d7qhlxuwcpbt">
                        Runs with your reusable times.
                      </span>
                    </span>
                    {runMode === 'saved' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-8674" data-mp="37jx5nx8w30lj">
                        <CheckCircle2 size={14} data-id="element-8675" data-mp="13h532dc8lnzc" />
                      </span>}
                  </button>
                  <button type="button" onClick={() => setRunMode('custom')} aria-pressed={runMode === 'custom'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'custom' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-8676" data-mp="yh96v09s70uc">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'custom' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-8677" data-mp="jsmkielql8tm">
                      <Settings size={18} data-id="element-8678" data-mp="aszf7hpvziva" />
                    </span>
                    <span className="min-w-0" data-id="element-8679" data-mp="5t3kya6zr5sx">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-8680" data-mp="5rs7tjrnolp5">
                        Custom
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-8681" data-mp="3mvhlclwsezqs">
                        Its own schedule.
                      </span>
                    </span>
                    {runMode === 'custom' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-8682" data-mp="oj1atvywbu4">
                        <CheckCircle2 size={14} data-id="element-8683" data-mp="5mu9l06m6ea8" />
                      </span>}
                  </button>
                </div>
              </div>

              {/* Which times? (only for saved-time mode) */}
              {runMode === 'saved' && <div data-id="element-8684" data-mp="391q9h6vl33rw">
                  <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8685" data-mp="3e3fmut6a3tj5">
                    Which times?
                  </h3>
                  <p className="text-xs text-neutral-400 mt-0.5 mb-3" data-id="element-8686" data-mp="3iuxm9csnlm20">
                    Pick the saved time(s) this runs with. Manage them in Pulse
                    Settings.
                  </p>
                  <div className="space-y-2.5" data-id="element-8687" data-mp="2qca8yjjwfbbh">
                    {PULSE_SAVED_TIMES.map(t => {
                const selected = selectedTimes.includes(t.id);
                return <button key={t.id} type="button" onClick={() => toggleTime(t.id)} aria-pressed={selected} className={`w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-colors ${selected ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-8688" data-mp="2de2bk7ej5i1s">
                          <span className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${selected ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-8689" data-mp="1zdhxtpnh2cf5">
                            <Clock size={18} data-id="element-8690" data-mp="29bxjve7n6vhs" />
                          </span>
                          <span className="min-w-0 flex-1" data-id="element-8691" data-mp="299upyj3dci7s">
                            <span className="block text-sm font-bold text-neutral-900" data-id="element-8692" data-mp="24bjczzhs1rlc">
                              {t.label}
                            </span>
                            <span className="block text-sm text-neutral-500 truncate" data-id="element-8693" data-mp="3fsswun4tiln6">
                              {t.cadence}
                            </span>
                          </span>
                          {selected && <CheckCircle2 size={18} className="text-neutral-900 shrink-0" data-id="element-8694" data-mp="3sb5a3xhkikim" />}
                        </button>;
              })}
                  </div>
                </div>}

              {/* Destination */}
              <div data-id="element-8695" data-mp="3idh42beihql5">
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8696" data-mp="3iczc3qyvtyj1">
                  Destination
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed mt-1" data-id="element-8697" data-mp="9z8jnqm58642">
                  Sent via the destination set on the saved time(s) above —
                  manage it in Pulse Settings.
                </p>
              </div>

              {/* Advanced settings */}
              <div className="border-t border-neutral-100 pt-5" data-id="element-8698" data-mp="54cmkc21usk3">
                <button type="button" onClick={() => setAdvancedOpen(v => !v)} aria-expanded={advancedOpen} className="flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-neutral-600 transition-colors" data-id="element-8699" data-mp="sup3udj6q1nc">
                  <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-0' : '-rotate-90'}`} data-id="element-8700" data-mp="8x59wh96ks03" />
                  Advanced settings
                </button>
                <AnimatePresence initial={false} data-id="element-8701" data-mp="3vc6ua2amdzro">
                  {advancedOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-8702" data-mp="3qcu6nfbxcoo1">
                      <div className="pt-4 space-y-3 text-sm text-neutral-500" data-id="element-8703" data-mp="hybvh4qost07">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-8704" data-mp="cx5wip6fo9i8">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-8705" data-mp="32oaxyyoyq9vf" />
                          Skip runs when there's nothing to report
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-8706" data-mp="1d3rji334h5og">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-8707" data-mp="1i4yvyfg7mt1g" />
                          Include links to the source items
                        </label>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-8708" data-mp="20tvmq9cef3ex">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-8709" data-mp="25ut4wlxerxgf">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-8710" data-mp="bhk37gb3tg8q">
                Create pulse
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Create-a-journey slide-over panel ────────────────────────────────────────
// A right-anchored sheet for spinning up a new journey: name, space, status,
// an optional overview that seeds journey.md, and a check-in cadence. Slides in
// from the right over a dimmed backdrop, mirroring the pulse builder so the two
// creation flows feel like one family.
const JOURNEY_SPACES = ['Everyone', 'Giga Main', 'Marketing', 'Product', 'Sales'];
const JOURNEY_STATUSES = ['Unset', 'In focus', 'Active', 'Paused', 'Done'];
const JOURNEY_CADENCES = ['No check-ins', 'Daily', 'Weekly', 'Every 2 weeks', 'Monthly'];
function JourneyField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return <div className="relative" data-id="element-8711" data-mp="2z2l898rqvlg1">
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium text-neutral-900 bg-white focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] cursor-pointer" data-id="element-8712" data-mp="2z1js4pvvg8rg">
        {options.map(o => <option key={o} value={o} data-id="element-8713" data-mp="dnlmg7icaad5">
            {o}
          </option>)}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8714" data-mp="8qbpm6smewfa" />
    </div>;
}
function JourneyCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [status, setStatus] = useState('Unset');
  const [overview, setOverview] = useState('');
  const [cadence, setCadence] = useState('No check-ins');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-8715" data-mp="34o2slql1tujs">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-8716" data-mp="34ouda6d3y7va">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-8717" data-mp="39m4eskdru9zh" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a journey" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-8718" data-mp="3ocboaalswio1">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-8719" data-mp="29d4lg1gycf6e">
              <div data-id="element-8720" data-mp="24fcw9f7oz75w">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-8721" data-mp="24hyz51jn0yhs">
                  New journey
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-8722" data-mp="2ef3qdfscbk5g">
                  Create a journey
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-8723" data-mp="1lp4x187450uz">
                <X size={20} data-id="element-8724" data-mp="20fc7b0myvdkl" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-8725" data-mp="1vl7wu7ehsner">
              {/* Name */}
              <div data-id="element-8726" data-mp="17vlx9iu7k668">
                <label htmlFor="journey-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8727" data-mp="12yn6995ucfc9">
                  Name
                </label>
                <input id="journey-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q3 Launch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-8728" data-mp="68thxzlwbrxm" />
              </div>

              {/* Space */}
              <div data-id="element-8729" data-mp="owoph1qx09ia">
                <label className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8730" data-mp="ox89co04l7yy">
                  Space
                </label>
                <JourneyField label="Space" value={space} onChange={setSpace} options={JOURNEY_SPACES} data-id="element-8731" data-mp="xyepux2ryis7" />
              </div>

              {/* Status */}
              <div data-id="element-8732" data-mp="12w7rrdx1oqmo">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-8733" data-mp="12vewpa7g7pkd">
                  Status
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8734" data-mp="34omagf7exz45">
                  How active this journey currently is.
                </p>
                <JourneyField label="Status" value={status} onChange={setStatus} options={JOURNEY_STATUSES} data-id="element-8735" data-mp="2x64wnwsi7y5d" />
              </div>

              {/* Overview */}
              <div data-id="element-8736" data-mp="2nfa7ajjyoifp">
                <label htmlFor="journey-overview" className="block text-sm font-semibold text-neutral-900" data-id="element-8737" data-mp="2ngld0owyw09g">
                  Overview
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8738" data-mp="3g47fftugkyuq">
                  Optional — seeds the journey's journey.md. You can edit it
                  later.
                </p>
                <textarea id="journey-overview" value={overview} onChange={e => setOverview(e.target.value)} placeholder="What this journey is about, goals, context…" rows={6} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-8739" data-mp="3b3bdhlto3mry" />
              </div>

              {/* Check-in cadence */}
              <div className="border-t border-neutral-100 pt-6" data-id="element-8740" data-mp="1s66l28r4yv6e">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-8741" data-mp="1x5j7wti0c28z">
                  Check-in cadence
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2 leading-relaxed" data-id="element-8742" data-mp="1i78tsy2bsnjp">
                  How often your assistant should proactively check in on this
                  journey. Leave as “No check-ins” to keep it passive.
                </p>
                <JourneyField label="Check-in cadence" value={cadence} onChange={setCadence} options={JOURNEY_CADENCES} data-id="element-8743" data-mp="1e6otjlmgh3tc" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-8744" data-mp="zfy2wgwloart">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-8745" data-mp="14ej1yd048oeo">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-8746" data-mp="uksglgzacu37">
                Create journey
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function AssistantCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [description, setDescription] = useState('');
  const [identity, setIdentity] = useState('');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-8747" data-mp="18lfvv34ydkfk">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-8748" data-mp="18m7ukmtujya2">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-8749" data-mp="2fwc41qdfnp1e" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create an assistant" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-8750" data-mp="2tvtx4hxrhogk">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-8751" data-mp="2b03semyg2dyz">
              <div data-id="element-8752" data-mp="1dgsld0a2t8aj">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-8753" data-mp="1ihg9ne6x7b12">
                  New assistant
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-8754" data-mp="1axfh59m0772m">
                  Create an assistant
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-8755" data-mp="10zhswm459aa0">
                <X size={20} data-id="element-8756" data-mp="rzcn2gelmjxy" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-8757" data-mp="n8mg4yqiy915">
              {/* Name */}
              <div data-id="element-8758" data-mp="i99xkq6oro4d">
                <label htmlFor="assistant-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8759" data-mp="i8qf98d4ixpt">
                  Name
                </label>
                <input id="assistant-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Glitch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-8760" data-mp="d8luv793hiub" />
              </div>

              {/* Space */}
              <div data-id="element-8761" data-mp="1vgeo4hml5opa">
                <label htmlFor="assistant-space" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8762" data-mp="1vj0sk8e6jo3a">
                  Space
                </label>
                <div className="relative" data-id="element-8763" data-mp="25g5imjb5c3pd">
                  <select id="assistant-space" value={space} onChange={e => setSpace(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] bg-white" data-id="element-8764" data-mp="1hp9j100g847k">
                    <option value="Everyone" data-id="element-8765" data-mp="1qjee5vmm81sw">Everyone</option>
                    <option value="Private" data-id="element-8766" data-mp="1lpa9jj0to5uz">Private</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-neutral-400" data-id="element-8767" data-mp="y07kvr7rtcqz">
                    <ChevronDown size={16} data-id="element-8768" data-mp="tzf84nzowppy" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-8769" data-mp="om75l1qnve4l">
                <label htmlFor="assistant-description" className="block text-sm font-semibold text-neutral-900" data-id="element-8770" data-mp="ompbkq34l6pp">
                  Description
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8771" data-mp="ewnl5rnavc7m">
                  Short summary shown on the assistants list.
                </p>
                <textarea id="assistant-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="What this assistant does" rows={3} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-8772" data-mp="12njfowj54lik" />
              </div>

              {/* Identity */}
              <div data-id="element-8773" data-mp="3dgdwssveaay1">
                <label htmlFor="assistant-identity" className="block text-sm font-semibold text-neutral-900" data-id="element-8774" data-mp="3id4etlqrfgm3">
                  Identity
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8775" data-mp="1otubf6aly133">
                  The assistant's system prompt — who it is and how it behaves.
                  You can refine this later.
                </p>
                <textarea id="assistant-identity" value={identity} onChange={e => setIdentity(e.target.value)} placeholder="Describe who this assistant is and how it should behave..." rows={8} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-8776" data-mp="1f2re8fksu693" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-8777" data-mp="r86ord3yex4r">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-8778" data-mp="2qjhb383c1lq1">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-8779" data-mp="2t3np68lnuskb">
                Create assistant
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Conversations view ───────────────────────────────────────────────────────
type ConvOwner = 'user' | 'assistant';
type ConvScope = 'private' | 'shared';
type ConvSource = 'cli' | 'web' | 'slack';
type ConversationItem = {
  title: string;
  preview: string;
  time: string;
  owner: ConvOwner;
  scope: ConvScope;
  source: ConvSource;
  // Author of the conversation — drives the avatar filter row.
  authorId: string;
};
const CONV_AUTHORS: {
  id: string;
  name: string;
  initials: string;
  color: string;
}[] = [{
  id: 'nt',
  name: 'Nina Torres',
  initials: 'NT',
  color: 'bg-[#1A1410] text-white'
}, {
  id: 'nc',
  name: 'Noah Cole',
  initials: 'NC',
  color: 'bg-indigo-600 text-white'
}, {
  id: 'no',
  name: 'Nadia Owens',
  initials: 'NO',
  color: 'bg-rose-700 text-white'
}, {
  id: 'ev',
  name: 'Evan Vargas',
  initials: 'Ev',
  color: 'bg-rose-800 text-white'
}, {
  id: 'gm',
  name: 'Grace Mwangi',
  initials: 'GM',
  color: 'bg-sky-700 text-white'
}, {
  id: 'gg',
  name: 'Giga (Assistant)',
  initials: 'GG',
  color: 'bg-neutral-900 text-white'
}];
const CONVERSATIONS: ConversationItem[] = [{
  title: 'Q3 renewal strategy',
  preview: 'Here is the draft brief for the Northwind renewal…',
  time: '10 mins ago',
  owner: 'user',
  scope: 'private',
  source: 'web',
  authorId: 'nt'
}, {
  title: 'Weekly standup recap',
  preview: 'Summarized the engineering sync and posted to Slack.',
  time: '1 hour ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}, {
  title: 'Ad spend audit results',
  preview: 'Paused 3 ad sets under 0.8× ROAS and exported a PDF.',
  time: 'Yesterday',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'nc'
}, {
  title: 'Support ticket triage',
  preview: 'Replied to 6 tickets and escalated #4092 to eng.',
  time: 'Yesterday',
  owner: 'assistant',
  scope: 'private',
  source: 'cli',
  authorId: 'gg'
}, {
  title: 'Pipeline cleanup',
  preview: 'Updated 14 stale deals and flagged 2 at risk.',
  time: '2 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'no'
}, {
  title: 'Onboarding doc for Acme',
  preview: 'Created the Notion onboarding doc and invited the client.',
  time: '3 days ago',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'ev'
}, {
  title: 'Deploy hotfix to staging',
  preview: 'Ran the release script and confirmed the build is green.',
  time: '3 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'gm'
}, {
  title: 'Customer NPS digest',
  preview: 'Pulled this week’s responses and flagged 2 detractors.',
  time: '4 days ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}];
function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel
}: {
  options: {
    value: T;
    label: string;
    icon?: React.ReactNode;
  }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return <div role="tablist" aria-label={ariaLabel} className="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-neutral-200 bg-neutral-50" data-id="element-8780" data-mp="3jw4mi94qrppq">
      {options.map(opt => {
      const selected = opt.value === value;
      return <button key={opt.value} role="tab" aria-selected={selected} onClick={() => onChange(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-8781" data-mp="gg79ztj3y5o8">
            {opt.icon}
            {opt.label}
          </button>;
    })}
    </div>;
}
function ConversationsView() {
  const [owner, setOwner] = useState<ConvOwner>('user');
  const [scope, setScope] = useState<ConvScope>('private');
  const [source, setSource] = useState<'all' | ConvSource>('all');
  const [authorId, setAuthorId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const filtered = CONVERSATIONS.filter(c => {
    if (c.owner !== owner) return false;
    if (c.scope !== scope) return false;
    if (source !== 'all' && c.source !== source) return false;
    if (authorId && c.authorId !== authorId) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!c.title.toLowerCase().includes(q) && !c.preview.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sourceFilters: {
    value: 'all' | ConvSource;
    label: string;
  }[] = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'cli',
    label: 'CLI'
  }, {
    value: 'web',
    label: 'Web'
  }, {
    value: 'slack',
    label: 'Slack'
  }];
  return <div className="flex flex-col" data-id="element-8782" data-mp="9yw0yrpm2akb">
      {/* Header row: title + Users/Assistants + Private/Shared */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-8783" data-mp="2bu391sysvmr0">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8784" data-mp="26wte5xsn6ivs">Conversations</h1>
        <div className="flex items-center gap-3 flex-wrap" data-id="element-8785" data-mp="2lko81hidytag">
          <SegmentedToggle ariaLabel="Filter by owner" value={owner} onChange={setOwner} options={[{
          value: 'user',
          label: 'Users',
          icon: <UserCircle size={15} data-id="element-8787" data-mp="1sxk4lgyxyn0s" />
        }, {
          value: 'assistant',
          label: 'Assistants',
          icon: <Sparkles size={15} data-id="element-8788" data-mp="1o0ldz8ehky7c" />
        }]} data-id="element-8786" data-mp="2gmcv2xwso2o0" />
          <div className="hidden sm:block w-px h-6 bg-neutral-200" data-id="element-8789" data-mp="22wj68flahiea" />
          <SegmentedToggle ariaLabel="Filter by visibility" value={scope} onChange={setScope} options={[{
          value: 'private',
          label: 'Private',
          icon: <Lock size={14} data-id="element-8791" data-mp="3e8tty7l6eobv" />
        }, {
          value: 'shared',
          label: 'Shared',
          icon: <Users size={14} data-id="element-8792" data-mp="39do6v5g1f3m7" />
        }]} data-id="element-8790" data-mp="1xwofcc1ikst8" />
        </div>
      </div>

      {/* Filter row: author avatars + source filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-8793" data-mp="3lu5x8vxyh0ax">
        <div className="flex items-center gap-2" data-id="element-8794" data-mp="37uehapnvp9ua">
          <button onClick={() => setAuthorId(null)} aria-label="All authors" aria-pressed={authorId === null} className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${authorId === null ? 'bg-neutral-200 text-neutral-700' : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'}`} data-id="element-8795" data-mp="37vprp8btxfud">
            <LayoutGrid size={16} data-id="element-8796" data-mp="8ehvou6j5tgq" />
          </button>
          <div className="flex items-center gap-2" data-id="element-8797" data-mp="3qu25j7dordwa">
            {CONV_AUTHORS.map(a => {
            const selected = authorId === a.id;
            return <button key={a.id} onClick={() => setAuthorId(selected ? null : a.id)} title={a.name} aria-label={a.name} aria-pressed={selected} className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 transition-all ${a.color} ${selected ? 'ring-2 ring-offset-2 ring-[#EA580C]' : authorId ? 'opacity-40 hover:opacity-100' : 'hover:opacity-90'}`} data-id="element-8798" data-mp="3ut13u94wwcer">
                  {a.initials}
                </button>;
          })}
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-8799" data-mp="3ar4oi4p1tekm">
          <div className="relative hidden md:block" data-id="element-8800" data-mp="35stffw6kv83g">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-8801" data-mp="35tcxdcw89whh" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search conversations..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-56" data-id="element-8802" data-mp="2i38vb9you4sl" />
          </div>
          <div role="tablist" aria-label="Filter by source" className="inline-flex items-center gap-1" data-id="element-8803" data-mp="2mzz8npj3ymab">
            {sourceFilters.map(s => {
            const selected = source === s.value;
            return <button key={s.value} role="tab" aria-selected={selected} onClick={() => setSource(s.value)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}`} data-id="element-8804" data-mp="1ubai2kfohzct">
                  {s.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8805" data-mp="4twvfbtuqb59">
        {filtered.length === 0 ? <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-8806" data-mp="xkyalcxjo6os">
            <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-8807" data-mp="xikfj42v6uh8">
              <MessageSquare size={18} className="text-neutral-400" data-id="element-8808" data-mp="ojqmtosnmo8y" />
            </div>
            <p className="text-sm font-semibold text-neutral-700" data-id="element-8809" data-mp="34bnzmez017jq">
              No conversations match these filters
            </p>
            <p className="text-xs text-neutral-500 mt-1" data-id="element-8810" data-mp="39b90ww1n5rzm">
              Try a different owner, source, or clear the author selection.
            </p>
          </div> : <div className="divide-y divide-neutral-100" data-id="element-8811" data-mp="19vfvc7qxy0tn">
            {filtered.map((c, i) => {
          const author = CONV_AUTHORS.find(a => a.id === c.authorId);
          return <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8812" data-mp="152cynqqvab98" data-mpi={i}>
                  <div className="flex items-center gap-4 min-w-0" data-id="element-8813" data-mp="w4au517xd0h7">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${author?.color ?? 'bg-neutral-100 text-neutral-500'}`} data-id="element-8814" data-mp="w1evtbs609s8">
                      {author?.initials ?? '?'}
                    </div>
                    <div className="flex flex-col min-w-0" data-id="element-8815" data-mp="i51k6jabb2lo">
                      <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8816" data-mp="2gjcpsj1hak1o">
                        {c.title}
                      </span>
                      <span className="text-xs text-neutral-500 truncate" data-id="element-8817" data-mp="2cij31szrvbad">
                        {c.preview}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0" data-id="element-8818" data-mp="381un419naic">
                    <span className="hidden sm:inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 capitalize" data-id="element-8819" data-mp="7zjlgz9krupl">
                      {c.source}
                    </span>
                    <span className="text-xs font-medium text-neutral-400" data-id="element-8820" data-mp="3bi3fo7btdan1">
                      {c.time}
                    </span>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
}
function EmptyStateOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (view: string) => void;
}) {
  // DEBUG: mirror the connection + activity debug toggles so the populated vs
  // empty states can be previewed. `hasTasks` follows the activity toggle —
  // when Giga has been doing work, it also has things awaiting your review.
  const [toolsConnected, setToolsConnected] = useState(false);
  const [hasActivity, setHasActivity] = useState(false);
  const hasTasks = hasActivity;
  // Tasks: the handful of things Giga paused on and needs the user's call.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  const recentActivity = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    tool: 'gmail',
    type: 'Chat',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    tool: 'hubspot',
    type: 'Journey',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    tool: 'slack',
    type: 'Pulse',
    status: 'done',
    time: '1h ago'
  }];
  return <div className="flex flex-col h-full" data-id="element-8821" data-mp="20u7vk6ltxfb7">
      <div className="flex items-center justify-between mb-8" data-id="element-8822" data-mp="20teyjxcoa469">
        <h2 className="text-2xl font-bold text-neutral-900" data-id="element-8823" data-mp="25lpwwvrrlhwp">Overview</h2>
        <div className="flex items-center gap-2" data-id="element-8824" data-mp="1qwjyha0r3vpv">
          {/* DEBUG toggles — preview populated vs empty states */}
          <button onClick={() => setToolsConnected(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${toolsConnected ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-8825" data-mp="1vv3l7cn65bja">
            {toolsConnected ? 'tools: connected' : 'tools: none'}
          </button>
          <button onClick={() => setHasActivity(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${hasActivity ? 'border-[#EA580C]/40 bg-[#EA580C]/[0.06] text-[#C2410C]' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-8826" data-mp="3jt5riq3ogb0j">
            {hasActivity ? 'activity: on' : 'activity: off'}
          </button>
          <button className="px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-8827" data-mp="6e9j7sidmptg">
            Customize
          </button>
        </div>
      </div>

      {/* ── Core Areas ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6 mb-8" data-id="element-8828" data-mp="2n3wtnolyn436">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-8829" data-mp="2i421lhqg88gj">
          {/* Conversations */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-8830" data-mp="xlzxnnojtzt7">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-8831" data-mp="12kuo9bsrcsnv">
              <div className="flex items-center gap-2" data-id="element-8832" data-mp="16m678yh4mfn3">
                <MessageSquare size={16} className="text-neutral-500" data-id="element-8833" data-mp="16lej28puzk6u" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8834" data-mp="1be7s38sd1eoq">
                  Conversations
                </h3>
              </div>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-8835" data-mp="1lbc5aj489zte">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-8836" data-mp="1qaygkn0hx5zk">
                No conversations yet.
              </span>
            </div>
          </div>

          {/* Journeys */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-8837" data-mp="3s21t5hty4umr">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-8838" data-mp="3w3djxqok3lwj">
              <div className="flex items-center gap-2" data-id="element-8839" data-mp="3d6v93ejs1gpu">
                <Map size={16} className="text-neutral-500" data-id="element-8840" data-mp="3d5k3d96rtyw3" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8841" data-mp="3878x18foq6im">
                  Journeys
                </h3>
              </div>
              <button className="text-xs font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors" data-id="element-8842" data-mp="3m1t4qteb77kv">
                Pinned Journeys <ChevronDown size={12} data-id="element-8843" data-mp="2u7r54k73ed51" />
              </button>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-8844" data-mp="2p9pn82lekjv5">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-8845" data-mp="338z28yxvh7xj">
                No pinned journeys.
              </span>
            </div>
          </div>
        </div>

        {/* Assistants */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-8846" data-mp="iiq4b1vs8rp8">
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-8847" data-mp="dkol8gycwsdr">
            <div className="flex items-center gap-2" data-id="element-8848" data-mp="m0nvvbrnp9gn">
              <UserCircle size={16} className="text-neutral-500" data-id="element-8849" data-mp="m308raik4tmu" />
              <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8850" data-mp="q3afwqi35q3a">
                Assistants
              </h3>
            </div>
          </div>
          <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-8851" data-mp="h1t4vmjjcelg">
            <span className="text-sm text-neutral-400 font-medium" data-id="element-8852" data-mp="14rf1q3cfqhqa">
              No assistants configured yet.
            </span>
          </div>
        </div>
      </div>

      {/* ── Tasks ─────────────────────────────────────────────────────────
                      Work Giga teed up that needs your call. Populates with the activity
                      toggle; otherwise shows a calm "your tasks live here" empty state. */}
      <div className="mb-8" data-id="element-8853" data-mp="1npzoxkb7h2yn">
        <div className="flex items-center justify-between mb-4" data-id="element-8854" data-mp="1noonhr58g7ap">
          <div className="flex items-center gap-2.5" data-id="element-8855" data-mp="1sh9dehl0q08y">
            <h3 className="text-base font-semibold text-neutral-900" data-id="element-8856" data-mp="1sj2ms4n0mykn">Tasks</h3>
            {hasTasks && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-8857" data-mp="2ugql5c7pbw20">
                {tasks.length}
              </span>}
          </div>
          {hasTasks && <span className="text-xs font-medium text-neutral-400" data-id="element-8858" data-mp="395mynosj21a0">
              Needs your review
            </span>}
        </div>
        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-8859" data-mp="2bj642rb6i1ry">
          {!hasTasks ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-8860" data-mp="2gi0otyspi0ep">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-8861" data-mp="2lfsndbnuwkrv">
                <ListChecks size={18} className="text-neutral-400" data-id="element-8862" data-mp="2pjgvpwkbbke6" />
              </span>
              <div data-id="element-8863" data-mp="pbehs97f9b0">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-8864" data-mp="ptft3aq4dps">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-8865" data-mp="1p9h8ph2fmr3o">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-8866" data-mp="1gg45nmanm93v">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-8867" data-mp="1bj3y16y5oyan" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-8868" data-mp="16k0vpu9k966d">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-8869" data-mp="xjciv2vwlbv2" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-8870" data-mp="2mx7v4m01j2jx" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-8871" data-mp="3atckdawdwu10">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-8872" data-mp="3au46lv43dfem">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-8873" data-mp="31zzgxf0p2a0o">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-8874" data-mp="3pnzaempe4eiy">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-8875" data-mp="24c7i6glzn0qz" />
                  </button>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Activity + Connection containers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1" data-id="element-8876" data-mp="21satb1gvrx1c">
        {/* Recent activity */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-8877" data-mp="1wt7y08qpd56k">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-8878" data-mp="2at0r2appbxhp">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8879" data-mp="25u5vs9ajsggp">
              Recent activity
            </h3>
            {hasActivity && <button onClick={() => onNavigate('activity')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-8880" data-mp="1i5d62ukir4uj">
                View all
              </button>}
          </div>
          {hasActivity ? <div className="divide-y divide-neutral-100" data-id="element-8881" data-mp="1rvfzgvvjykte">
              {recentActivity.map((item, i) => <div key={i} className="px-5 py-3.5 flex items-start gap-3.5" data-id="element-8882" data-mp="1n1vblwnob77x" data-mpi={i}>
                  <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8883" data-mp="zcskly75g20r">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-8884" data-mp="181hnwt9pn89a" />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-8885" data-mp="1hxkqu1cdzaxa" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-8886" data-mp="1qubqenq5d7wm">
                    <div className="flex items-start justify-between gap-3" data-id="element-8887" data-mp="1vq0saxpn30w3">
                      <p className="text-sm font-medium text-neutral-900 leading-snug" data-id="element-8888" data-mp="20plxhpvek5h9">
                        {item.task}
                      </p>
                      <span className="text-xs text-neutral-400 shrink-0" data-id="element-8889" data-mp="25ogkl40ekg76">
                        {item.time}
                      </span>
                    </div>
                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-8890" data-mp="bc8klc98594j">
                      {item.type}
                    </span>
                  </div>
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-8891" data-mp="2fsdkgm7slizl">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-8892" data-mp="2auls5r38juuv">
                <Activity size={20} className="text-neutral-400" data-id="element-8893" data-mp="37jt3k6a54nl2" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-8894" data-mp="33k2hgfvk6vmv">
                No recent activity
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-8895" data-mp="2ylr8e7d38p5p">
                When Giga completes tasks, runs automations, or answers your
                questions, they will appear here.
              </p>
              <button onClick={onStartTask} className="px-4 py-2 bg-[#2B211A] hover:bg-[#1A1410] text-white rounded-lg text-sm font-medium transition-colors" data-id="element-8896" data-mp="1n250cotp4oid">
                Start a new chat
              </button>
            </div>}
        </div>

        {/* Connection state */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-8897" data-mp="1okvg6b6cz07c">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-8898" data-mp="1alm4not3ku9p">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8899" data-mp="1fn321acdhylj">
              Connected tools
            </h3>
            <button onClick={() => onNavigate('integrations')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-8900" data-mp="10u99an83blyx">
              Manage
            </button>
          </div>
          {toolsConnected ? <div className="divide-y divide-neutral-100" data-id="element-8901" data-mp="vwhk40ytydyf">
              {[{
            tool: 'slack',
            label: 'Slack',
            ok: true
          }, {
            tool: 'gmail',
            label: 'Gmail',
            ok: true
          }, {
            tool: 'notion',
            label: 'Notion',
            ok: false
          }].map((c, i) => <div key={i} className="px-5 py-3.5 flex items-center gap-3.5" data-id="element-8902" data-mp="3arg4o0re5xo" data-mpi={i}>
                  <span className="w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8903" data-mp="3uidh28w3fuau">
                    <img src={`https://cdn.simpleicons.org/${c.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" data-id="element-8904" data-mp="89m1nxq428lh" />
                  </span>
                  <span className="flex-1 text-sm font-medium text-neutral-900" data-id="element-8905" data-mp="m82f07ihaxcl">
                    {c.label}
                  </span>
                  {c.ok ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full pl-2 pr-2.5 py-1" data-id="element-8906" data-mp="vz6ur9fzam8h">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-8907" data-mp="r0lvbc8jw6l3" />
                      Connected
                    </span> : <button onClick={() => onNavigate('integrations')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full pl-2 pr-2.5 py-1 transition-colors" data-id="element-8908" data-mp="14xscj2dmlx87">
                      <AlertTriangle size={11} data-id="element-8909" data-mp="2cc29fv1ukcj0" />
                      Reconnect
                    </button>}
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-8910" data-mp="2hcqal9jsi9qy">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-8911" data-mp="2lbz207rcxvjc">
                <Plug size={20} className="text-neutral-400" data-id="element-8912" data-mp="1okn37r6y9b2h" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-8913" data-mp="1tmcd33ifrioz">
                Connect your tools
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-8914" data-mp="1xllbilof84qv">
                Giga works best when it can access your apps. Connect Slack,
                Gmail, Notion, and more to get started.
              </p>
              <button onClick={() => onNavigate('integrations')} className="px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg text-sm font-medium transition-colors" data-id="element-8915" data-mp="19xlgh9k2ts6h">
                View integrations
              </button>
            </div>}
        </div>
      </div>

      {/* ── Your Impact ─────────────────────────────────────────────────────────
          The high-level metrics moved down here so the top of the overview can
          focus on actionable areas (Conversations, Journeys, Assistants). */}
      <div className="mt-8 pt-8 border-t border-neutral-200" data-id="element-8916" data-mp="jh1doccmdu9w">
        <h3 className="text-base font-semibold text-neutral-900 mb-4" data-id="element-8917" data-mp="eohwl5eq4h0c">
          Your Impact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8918" data-mp="9qot4k4t213r">
          {[{
          label: 'Tasks Completed',
          value: hasActivity ? '128' : '0',
          note: hasActivity ? '+12% this week' : 'Ready for your first task',
          icon: CheckCircle2
        }, {
          label: 'Hours Saved',
          value: hasActivity ? '46' : '0',
          note: hasActivity ? '~6 days of work' : 'Let Giga do the heavy lifting',
          icon: Clock
        }, {
          label: 'Active Automations',
          value: hasActivity ? '4' : '0',
          note: hasActivity ? 'Running smoothly' : 'Create a pulse or journey',
          icon: Zap
        }].map((stat, i) => <div key={i} className={`bg-white rounded-xl border border-neutral-200 p-5 shadow-sm flex flex-col gap-3 transition-opacity ${hasActivity ? '' : 'opacity-60'}`} data-id="element-8919" data-mp="3bpwj6f43zas7" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-8920" data-mp="37q5injos5gah">
                <span className="text-sm font-medium text-neutral-500" data-id="element-8921" data-mp="32wkmlx6qywdx">
                  {stat.label}
                </span>
                <stat.icon size={16} className="text-neutral-400" data-id="element-8922" data-mp="2xw5995mgtovy" />
              </div>
              <div className="flex items-baseline gap-2" data-id="element-8923" data-mp="2t0zoia4sug9g">
                <span className="text-3xl font-bold text-neutral-900" data-id="element-8924" data-mp="2o0bnqwqrql21">
                  {stat.value}
                </span>
              </div>
              <span className="text-xs font-medium text-neutral-400" data-id="element-8925" data-mp="2jz9vx0z6lba3">
                {stat.note}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
// Giga-specific onboarding checklist. Each step maps to a real part of getting
// Giga working across your apps — not a generic marketing funnel. The dashboard
// section a step links to is used to jump the user straight there.
type OnboardingStep = {
  id: number;
  title: string;
  desc: string;
  cta: string;
  nav: string;
  done?: boolean;
};
const ONBOARDING_STEPS: OnboardingStep[] = [{
  id: 1,
  title: 'Create your Giga account',
  desc: 'Your workspace is ready — Giga is standing by.',
  cta: 'Done',
  nav: 'overview',
  done: true
}, {
  id: 2,
  title: 'Connect your tools',
  desc: 'Link Gmail, Notion, and the rest of your stack so Giga can act on your behalf across the tools you already use.',
  cta: 'Connect other tools',
  nav: 'integrations'
}, {
  id: 3,
  title: '@mention Giga in a chat',
  desc: 'Add Giga to any Slack channel and just @mention it to kick off your first task.',
  cta: 'Open conversations',
  nav: 'conversations'
}, {
  id: 4,
  title: 'Set up a Pulse',
  desc: 'Let Giga watch for something and act automatically — like a morning briefing or a reply nudge.',
  cta: 'Create a pulse',
  nav: 'pulses'
}, {
  id: 5,
  title: 'Build your first Journey',
  desc: 'Chain a few steps into a repeatable workflow Giga can run end to end.',
  cta: 'Create a journey',
  nav: 'journeys'
}];
function OnboardingWidget({
  onNavigate
}: {
  onNavigate: (nav: string) => void;
}) {
  const [expanded, setExpanded] = useState<number | null>(2);
  const [minimized, setMinimized] = useState(false);
  // Slack is required to create a Giga account, so by the time the user reaches
  // this dashboard it is always connected. Detected here (true) so the
  // "Connect your tools" step can show Slack as linked instead of asking again.
  const slackConnected = true;
  const completed = ONBOARDING_STEPS.filter(s => s.done).length;
  const pct = Math.round(completed / ONBOARDING_STEPS.length * 100);
  if (minimized) {
    return <button onClick={() => setMinimized(false)} aria-label="Open setup checklist" className="fixed bottom-6 left-6 md:left-[256px] z-50 flex items-center gap-2.5 h-11 pl-2.5 pr-4 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white shadow-xl transition-colors" data-id="element-8926" data-mp="2ecg58xb0ih0k">
        <span className="w-6 h-6 rounded-full bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-8927" data-mp="25c1gtp2r4lsu">
          <Sparkles size={13} className="text-white" data-id="element-8928" data-mp="2t3golxdarvbh" />
        </span>
        <span className="text-sm font-semibold" data-id="element-8929" data-mp="2x1nt9tuhl66b">Set up Giga · {pct}%</span>
      </button>;
  }
  return <div className="fixed bottom-6 left-6 md:left-[256px] z-50 w-80 bg-white rounded-2xl shadow-xl border border-[#E7E0D6] overflow-hidden" data-id="element-8930" data-mp="2jahkf75bhw6r">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 bg-[#F5EFE6] border-b border-[#E7E0D6]" data-id="element-8931" data-mp="3c0hvjnqew5i2">
        <div className="flex items-start justify-between mb-1" data-id="element-8932" data-mp="3bydhicystq42">
          <div className="flex items-center gap-2" data-id="element-8933" data-mp="3grq28c0cxcrj">
            <span className="w-6 h-6 rounded-md bg-[#2B211A] flex items-center justify-center shrink-0" data-id="element-8934" data-mp="1s4vl3xz7knsx">
              <Sparkles size={13} className="text-[#EA580C]" data-id="element-8935" data-mp="1n5iy9d8c7gqc" />
            </span>
            <h3 className="text-base font-bold text-[#2B211A]" data-id="element-8936" data-mp="1j4fjku20zqqp">
              Get Giga working
            </h3>
          </div>
          <button onClick={() => setMinimized(true)} aria-label="Minimize" className="text-neutral-400 hover:text-[#2B211A] transition-colors" data-id="element-8937" data-mp="1e648yh3wpc7f">
            <Minus size={16} data-id="element-8938" data-mp="2autoi0s29sq8" />
          </button>
        </div>
        <div className="flex items-center justify-between mb-3 pl-8" data-id="element-8939" data-mp="26trrzrpj3ury">
          <span className="text-sm text-neutral-500" data-id="element-8940" data-mp="2231l2a1gfjv5">
            {completed} of {ONBOARDING_STEPS.length} done
          </span>
          <span className="text-sm font-semibold text-[#EA580C]" data-id="element-8941" data-mp="1x35ga8kxnoej">{pct}%</span>
        </div>
        {/* Progress bar */}
        <div className="h-2 w-full bg-[#E7DCCB] rounded-full overflow-hidden ml-8 max-w-[calc(100%-2rem)]" data-id="element-8942" data-mp="2ul5irt817iat">
          <motion.div className="h-full bg-[#EA580C] rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${pct}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-8943" data-mp="2prks6m8rpqln" />
        </div>
      </div>

      {/* Accordion items */}
      <div className="flex flex-col px-2 py-2 max-h-[46vh] overflow-y-auto" data-id="element-8944" data-mp="370n2mmjsim0p">
        {ONBOARDING_STEPS.map(step => {
        const isOpen = expanded === step.id;
        return <div key={step.id} className="rounded-xl" data-id="element-8945" data-mp="3g3o6egl6o117">
              <button onClick={() => setExpanded(isOpen ? null : step.id)} className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F5EFE6] transition-colors text-left" data-id="element-8946" data-mp="3g120smhurvlm">
                <div className="flex items-center gap-3 min-w-0" data-id="element-8947" data-mp="3pv24ql1eeusr">
                  {step.done ? <span className="w-5 h-5 rounded-full bg-[#2B211A] flex items-center justify-center text-white shrink-0" data-id="element-8948" data-mp="3uqzlutkhk56l">
                      <Check size={12} strokeWidth={3} data-id="element-8949" data-mp="7n2w8wgtf1wi" />
                    </span> : <span className="w-5 h-5 rounded-full border-2 border-dashed border-[#C9BBA6] shrink-0" data-id="element-8950" data-mp="7ncn0m9opd39" />}
                  <span className={`text-sm font-medium truncate ${step.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-8951" data-mp="23o4x9qyv1n1a">
                    {step.title}
                  </span>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-neutral-400 shrink-0" data-id="element-8952" data-mp="28pasbvfztmdz" /> : <ChevronDown size={16} className="text-neutral-400 shrink-0" data-id="element-8953" data-mp="28nzndsata8la" />}
              </button>
              <AnimatePresence initial={false} data-id="element-8954" data-mp="2ncdpg10elt0s">
                {isOpen && !step.done && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-8955" data-mp="2ie2hjvto5sl7">
                    <div className="pl-11 pr-3 pb-3 pt-0.5" data-id="element-8956" data-mp="x2at80te0yyi">
                      <p className="text-sm text-neutral-500 leading-relaxed mb-3" data-id="element-8957" data-mp="x45c79l3mgzy">
                        {step.desc}
                      </p>
                      {/* Slack is connected during account creation, so on the
                       "Connect your tools" step we surface it as already-linked
                       (detected) rather than asking for it again, and offer a
                       separate button to connect the rest of the stack. */}
                      {step.id === 2 && slackConnected && <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[#E7E0D6] bg-[#F5EFE6] px-3 py-2" data-id="element-8958" data-mp="2q0ziiio283mc">
                          <span className="flex items-center gap-2 min-w-0" data-id="element-8959" data-mp="2gyq61n98e06b">
                            <img src="https://cdn.simpleicons.org/slack" alt="" aria-hidden="true" className="w-4 h-4 shrink-0 object-contain" data-id="element-8960" data-mp="2kza3krxpv5sz" />
                            <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-8961" data-mp="f73ffmt947ad">
                              Slack
                            </span>
                          </span>
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-id="element-8962" data-mp="333odn0mkp7ij">
                            <Check size={13} strokeWidth={3} data-id="element-8963" data-mp="2y5wjdzygh9b6" />
                            Connected
                          </span>
                        </div>}
                      <button onClick={() => onNavigate(step.nav)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-8964" data-mp="2oea3hqb6am23">
                        {step.cta}
                        <ArrowRight size={14} data-id="element-8965" data-mp="2fgzqo0ipiov2" />
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}